// src/components/NumberLine.tsx
/**
 * NumberLine Component (T-09: Orientation Toggle Enhanced + T-10: Subticks & Midpoint Helper + T-11: Constants Overlay + T-12: Secondary Number Line + T-13: Jump Indicators with Drag Support + T-18: Annotation Canvas + T-25: Locale Support)
 * 
 * Primary number line visualization component that renders an interactive SVG number line.
 * Integrates with Redux state and tick generation system.
 * 
 * Features:
 * - Adaptive tick generation based on zoom level
 * - Clickable labels for hide/show functionality
 * - Origin (zero) highlighting
 * - Multiple number types (integer, decimal, fraction, percent)
 * - Responsive design with proper viewport handling
 * - PAN & ZOOM INTERACTIONS (T-08):
 *   - Mouse drag to pan horizontally/vertically
 *   - Ctrl+wheel to zoom at cursor position
 *   - Touch drag and pinch for mobile
 *   - Smooth, responsive interactions
 * - ORIENTATION TOGGLE (T-09):
 *   - Horizontal ↔ vertical with preserved interactions
 *   - Natural pan direction mapping for each orientation
 *   - Proper tick and label positioning adaptation
 * - T-10: SUBTICKS & MIDPOINT HELPER:
 *   - Enhanced subtick generation with proper spacing heuristics
 *   - Right-click context menu for adding midpoints
 *   - Integration with custom ticks Redux slice
 *   - Visual feedback for custom ticks (purple color)
 * - T-11: CONSTANTS OVERLAY:
 *   - Mathematical constants (π, √2, etc.) with collision-free positioning
 *   - Configurable precision and visibility
 *   - Blue markers with connection lines to labels
 * - T-12: SECONDARY NUMBER LINE:
 *   - Secondary number line with independent scale factor
 *   - Different number type formatting for secondary line
 *   - Visual alignment lines connecting primary and secondary ticks
 *   - Green color scheme to distinguish from primary line
 * - T-13: JUMP INDICATORS WITH DRAG SUPPORT:
 *   - Interactive jump indicators for modeling addition/subtraction
 *   - Draggable handles for adjusting jump endpoints
 *   - Visual feedback during drag operations
 *   - Collision-free lane assignment for overlapping jumps
 * - T-18: ANNOTATION CANVAS:
 *   - Interactive annotation tools (text, pen, shapes, etc.)
 *   - Non-interfering with pan/zoom when annotation tools are active
 *   - Proper cursor styling for different annotation tools
 * - T-25: LOCALE SUPPORT:
 *   - Locale-aware number formatting with decimal separator support
 *   - Toggle between '.' and ',' decimal separators based on locale
 *   - Enhanced tick labels, secondary line values, constants, and jump indicators
 *   - Support for multiple locales (en-GB, en-US, de-DE, fr-FR, es-ES)
 */

import React, { useMemo, useRef, useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../types/redux';
import { generateTicks, Tick } from '../lib/ticks';
import { computeVisibleDomain, valueToPx, pxToValue } from '../lib/scale';
import { setLabelVisibility, clearSecondaryLabelOverrides } from '../store/slices/labelVisibilitySlice';
import { pan, zoomAt } from '../store/slices/viewportSlice';
// T-10: Import custom ticks functionality
import { addMidpoint, removeCustomTickByValue } from '../store/slices/customTicksSlice';
import { removeCustomTick as removeDNLCustomTick } from '../store/slices/dnlToolsSlice';
import { selectCustomTicksInDomain } from '../lib/ticks';
import MathRenderer from './MathRenderer';
// T-25: Import locale functionality
import { LocaleCode, formatNumberWithPrecision } from '../lib/locale';
import { selectLocale, selectConstantsPrecision, selectSubdivisionMode } from '../store/slices/preferencesSlice';
// T-11: Import constants functionality
import { 
  MATHEMATICAL_CONSTANTS, 
  getVisibleConstants, 
  createConstantLabel,
  calculateConstantPositions 
} from '../lib/constants';
import { useAppSelector } from '../hooks/redux';
// T-12: Import secondary line functionality
// Removed unused imports for legacy secondary line formatting
import SecondaryLineLayer from './SecondaryLineLayer';
// T-13: Import jump indicators functionality with drag support
import { deselectAllJumps, toggleJumpSelection, deleteSelectedJumps, updateJumpEndpoints, addJump, selectJumpDrawingMode, selectDefaultJumpLine } from '../store/slices/jumpIndicatorsSlice';
import { 
  calculateJumpLanes, 
  getLaneOffset,
  Jump
} from '../lib/jumps';
import { nanoid } from 'nanoid';
// T-13: Import jump drag functionality
import { 
  DragState, 
  initialDragState,
  getEventPosition,
  getDragCursor,
  calculateDragEndpoints
} from '../lib/jumpDrag';
// Phase 2: Import JumpIndicatorsRenderer for line-assigned jumps
import { JumpIndicatorsRenderer } from './JumpIndicatorsRenderer';
// Phase 7: Import RatioTableOverlay
import { RatioTableOverlay } from './dnl/RatioTableOverlay';

// T-15: Import drag snap functionality
import { useDragSnap } from '../hooks/useDragSnap';
import { SnapIndicator } from './SnapIndicator';
// DNL: Import DNL components
import { SparsePointsRenderer, LineLabelsRenderer, MappingArrowsLayer, AddPointDialog, EditPointDialog } from './dnl';

// Import fraction utilities for jump label formatting
import { approximate, formatFractionForMath } from '../lib/fractions';
import { fractionToLatex, formatScaleFactor, simplifyFraction, fractionToDecimal } from '../lib/scaleFactor';
import { addMultiplierMarker as addMultiplierMarkerAction, clearMultiplierMarkers as clearMultiplierMarkersAction } from '../store/slices/multiplierSlice';

// Dark mode theme colors
import { getThemeColors, getTickStyleByLevel as getThemeTickStyle } from '../lib/themeColors';

/**
 * Calculate a stable viewport that doesn't change during panning
 */
// Removed unused useStableViewport helper



// T-16: Import measurement functionality
import { MeasurementOverlay } from './MeasurementOverlay';
import { selectActiveTool } from '../store/slices/toolModeSlice';
import { 
  startMeasurement, 
  updateMeasurement, 
  completeMeasurement,
  deleteSelectedMeasurements,
  clearAllMeasurements,
  openAddPointDialog,
  closeAddPointDialog
} from '../store/slices/uiSlice';

// T-17: Import expression objects functionality
import ExpressionIndicator from './ExpressionIndicator';
import ExpressionWidget from './ExpressionWidget';
import { 
  selectExpressionObjects,
  selectExpressionsEnabled,
  showExpressionWidget,
  deselectAllExpressionObjects,
  deleteSelectedExpressionObjects,
  type ExpressionObject
} from '../store/slices/expressionsSlice';

  // T-18: Add annotation imports - Now handled by NumberLineCanvas.tsx
  
  // T-17: Enhanced context menu component with expression support
interface EnhancedContextMenuProps {
  position: { x: number; y: number };
  visible: boolean;
  onClose: () => void;
  onAddMidpoint?: () => void;
  onDeleteTick?: () => void;
  onEditTick?: () => void;  // NEW: Edit custom tick
  onDeleteTickOnly?: () => void;  // NEW: Delete only this tick (not linked)
  onDeleteTickAndLinked?: () => void;  // NEW: Delete this tick and linked tick
  onAddExpression?: () => void;  // NEW: T-17
  onAddMultiplier?: () => void;  // NEW: Multiplier arrow between lines
  onClearMultipliers?: () => void; // NEW: Clear multiplier markers
  onAddCustomPoint?: () => void;  // NEW: Custom point dialog
  midpointValue?: number;
  clickValue?: number;  // NEW: T-17 - Value at click position
  multiplierLabel?: string;
  hasLinkedTick?: boolean;  // NEW: Whether the tick has a linked tick
}

const EnhancedContextMenu: React.FC<EnhancedContextMenuProps> = ({
  position,
  visible,
  onClose,
  onAddMidpoint,
  onDeleteTick,
  onEditTick,  // NEW: Edit custom tick
  onDeleteTickOnly,  // NEW: Delete only this tick
  onDeleteTickAndLinked,  // NEW: Delete this tick and linked tick
  onAddExpression,  // NEW: T-17
  onAddMultiplier,
  onClearMultipliers,
  onAddCustomPoint,  // NEW: Custom point dialog
  midpointValue,
  clickValue,  // NEW: T-17
  multiplierLabel,
  hasLinkedTick,  // NEW: Whether the tick has a linked tick
}) => {
  const menuRef = useRef<HTMLDivElement>(null);
  // Initialize coords to position, will be adjusted after first render
  const [coords, setCoords] = useState<{ x: number; y: number } | null>(null);
  
  // Dark mode support for context menu
  const preferences = useSelector((state: RootState) => state.preferences);
  const menuIsDark = preferences.theme === 'dark';
  const menuColors = getThemeColors(menuIsDark);

  useEffect(() => {
    if (!visible) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleEscapeKey);
    }, 10);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, [visible, onClose]);

  // When the menu becomes visible or the anchor position changes,
  // position it so that it appears just ABOVE the cursor (or as close as
  // possible while staying within the viewport bounds).
  useEffect(() => {
    if (!visible) {
      setCoords(null);
      return;
    }

    // Use requestAnimationFrame to ensure the menu has been rendered
    // and we can measure its dimensions
    const rafId = requestAnimationFrame(() => {
      if (menuRef.current) {
        const rect = menuRef.current.getBoundingClientRect();
        const vw = window.innerWidth;
        const vh = window.innerHeight;

        // Position the menu at the click location (top-left corner at click point)
        // Then clamp to viewport bounds
        let x = position.x;
        let y = position.y;

        const PADDING = 8;
        
        // If menu would go off right edge, flip to left of cursor
        if (x + rect.width > vw - PADDING) {
          x = vw - rect.width - PADDING;
        }
        
        // If menu would go off bottom edge, show above cursor
        if (y + rect.height > vh - PADDING) {
          y = position.y - rect.height - 8;
        }
        
        // Final clamp to ensure it stays on screen
        x = Math.max(PADDING, Math.min(x, vw - rect.width - PADDING));
        y = Math.max(PADDING, Math.min(y, vh - rect.height - PADDING));

        setCoords({ x, y });
      } else {
        // Fallback: use the raw position
        setCoords(position);
      }
    });

    return () => cancelAnimationFrame(rafId);
  }, [visible, position.x, position.y]);

  if (!visible) return null;

  // Don't render until we have calculated coordinates
  // This prevents a flash of wrong position on first render
  const displayStyle: React.CSSProperties = coords ? {
    position: 'fixed',
    left: coords.x,
    top: coords.y,
    backgroundColor: menuColors.menuBackground,
    border: `1px solid ${menuColors.menuBorder}`,
    borderRadius: '4px',
    boxShadow: menuIsDark ? '0 2px 10px rgba(0,0,0,0.4)' : '0 2px 10px rgba(0,0,0,0.1)',
    zIndex: 1000,
    minWidth: '160px',
    color: menuColors.menuText,
  } : {
    position: 'fixed',
    left: position.x,
    top: position.y,
    backgroundColor: menuColors.menuBackground,
    border: `1px solid ${menuColors.menuBorder}`,
    borderRadius: '4px',
    boxShadow: menuIsDark ? '0 2px 10px rgba(0,0,0,0.4)' : '0 2px 10px rgba(0,0,0,0.1)',
    zIndex: 1000,
    minWidth: '160px',
    color: menuColors.menuText,
    visibility: 'hidden', // Hidden until positioned
  };

  return (
    <div
      ref={menuRef}
      className="context-menu"
      style={displayStyle}
      onClick={(e) => e.stopPropagation()}
    >
      {/* T-17: Add Expression option */}
      {onAddExpression && (
        <button
          className="context-menu-item"
          onClick={() => {
            onAddExpression();
            onClose();
          }}
          style={{
            display: 'block',
            width: '100%',
            padding: '8px 12px',
            border: 'none',
            backgroundColor: 'transparent',
            textAlign: 'left',
            cursor: 'pointer',
            fontSize: '14px',
            color: menuColors.menuText,
          }}
          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = menuIsDark ? '#4B5563' : '#f3f4f6'}
          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
        >
          ➕ Add Expression
          {clickValue !== undefined && (
            <span style={{ color: menuColors.menuTextSecondary, fontSize: '12px', marginLeft: '8px' }}>
              (at {clickValue.toFixed(2)})
            </span>
          )}
        </button>
      )}

      {onAddMultiplier && (
        <button
          className="context-menu-item"
          onClick={() => {
            onAddMultiplier();
            onClose();
          }}
          style={{
            display: 'block',
            width: '100%',
            padding: '8px 12px',
            border: 'none',
            backgroundColor: 'transparent',
            textAlign: 'left',
            cursor: 'pointer',
            fontSize: '14px',
            color: menuColors.menuText,
          }}
          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = menuIsDark ? '#4B5563' : '#f3f4f6'}
          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
        >
          ↕️ Show Multiplier
          {multiplierLabel && (
            <span style={{ color: menuColors.menuTextSecondary, fontSize: '12px', marginLeft: '8px' }}>
              ({multiplierLabel})
            </span>
          )}
        </button>
      )}

      {onAddMidpoint && (
        <button
          className="context-menu-item"
          onClick={() => {
            onAddMidpoint();
            onClose();
          }}
          style={{
            display: 'block',
            width: '100%',
            padding: '8px 12px',
            border: 'none',
            backgroundColor: 'transparent',
            textAlign: 'left',
            cursor: 'pointer',
            fontSize: '14px',
            color: menuColors.menuText,
          }}
          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = menuIsDark ? '#4B5563' : '#f3f4f6'}
          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
        >
          ➕ Add Midpoint
          {midpointValue !== undefined && (
            <span style={{ color: menuColors.menuTextSecondary, fontSize: '12px', marginLeft: '8px' }}>
              ({midpointValue})
            </span>
          )}
        </button>
      )}
      
      {onAddCustomPoint && (
        <button
          className="context-menu-item"
          onClick={() => {
            onAddCustomPoint();
            onClose();
          }}
          style={{
            display: 'block',
            width: '100%',
            padding: '8px 12px',
            border: 'none',
            backgroundColor: 'transparent',
            textAlign: 'left',
            cursor: 'pointer',
            fontSize: '14px',
            color: menuColors.menuText,
          }}
          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = menuIsDark ? '#4B5563' : '#f3f4f6'}
          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
        >
          📍 Add Custom Point...
          {clickValue !== undefined && (
            <span style={{ color: menuColors.menuTextSecondary, fontSize: '12px', marginLeft: '8px' }}>
              (at {clickValue.toFixed(2)})
            </span>
          )}
        </button>
      )}
      
      {onEditTick && (
        <button
          className="context-menu-item"
          onClick={() => {
            onEditTick();
            onClose();
          }}
          style={{
            display: 'block',
            width: '100%',
            padding: '8px 12px',
            border: 'none',
            backgroundColor: 'transparent',
            textAlign: 'left',
            cursor: 'pointer',
            fontSize: '14px',
            color: menuColors.menuText,
          }}
          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = menuIsDark ? '#4B5563' : '#f3f4f6'}
          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
        >
          ✏️ Edit Point
        </button>
      )}
      
      {hasLinkedTick ? (
        <>
          {onDeleteTickOnly && (
            <button
              className="context-menu-item"
              onClick={() => {
                onDeleteTickOnly();
                onClose();
              }}
              style={{
                display: 'block',
                width: '100%',
                padding: '8px 12px',
                border: 'none',
                backgroundColor: 'transparent',
                textAlign: 'left',
                cursor: 'pointer',
                fontSize: '14px',
                color: '#dc2626',
              }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = menuIsDark ? '#4B5563' : '#f3f4f6'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
            >
              🗑️ Delete This Point Only
            </button>
          )}
          {onDeleteTickAndLinked && (
            <button
              className="context-menu-item"
              onClick={() => {
                onDeleteTickAndLinked();
                onClose();
              }}
              style={{
                display: 'block',
                width: '100%',
                padding: '8px 12px',
                border: 'none',
                backgroundColor: 'transparent',
                textAlign: 'left',
                cursor: 'pointer',
                fontSize: '14px',
                color: '#dc2626',
              }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = menuIsDark ? '#4B5563' : '#f3f4f6'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
            >
              🗑️ Delete Both Points
            </button>
          )}
        </>
      ) : (
        <>
          {onDeleteTickOnly && (
            <button
              className="context-menu-item"
              onClick={() => {
                onDeleteTickOnly();
                onClose();
              }}
              style={{
                display: 'block',
                width: '100%',
                padding: '8px 12px',
                border: 'none',
                backgroundColor: 'transparent',
                textAlign: 'left',
                cursor: 'pointer',
                fontSize: '14px',
                color: '#dc2626',
              }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = menuIsDark ? '#4B5563' : '#f3f4f6'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
            >
              🗑️ Delete Point
            </button>
          )}
          {onDeleteTick && (
            <button
              className="context-menu-item"
              onClick={() => {
                onDeleteTick();
                onClose();
              }}
              style={{
                display: 'block',
                width: '100%',
                padding: '8px 12px',
                border: 'none',
                backgroundColor: 'transparent',
                textAlign: 'left',
                cursor: 'pointer',
                fontSize: '14px',
                color: '#dc2626',
              }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = menuIsDark ? '#4B5563' : '#f3f4f6'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
            >
              🗑️ Delete Custom Tick
            </button>
          )}
        </>
      )}

      {onClearMultipliers && (
        <button
          className="context-menu-item"
          onClick={() => {
            onClearMultipliers();
            onClose();
          }}
          style={{
            display: 'block',
            width: '100%',
            padding: '8px 12px',
            border: 'none',
            backgroundColor: 'transparent',
            textAlign: 'left',
            cursor: 'pointer',
            fontSize: '14px',
            color: menuColors.menuText,
          }}
          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = menuIsDark ? '#4B5563' : '#f3f4f6'}
          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
        >
          🧹 Clear Multipliers
        </button>
      )}
    </div>
  );
};

// T-10: Enhanced context menu state interface
interface ContextMenuState {
  visible: boolean;
  position: { x: number; y: number };
  mode: 'add-midpoint' | 'delete-tick' | 'edit-tick';
  intervalValues?: { start: number; end: number };
  tickToDelete?: any; // Reference to the custom tick to delete
  tickToEdit?: any; // Reference to the custom tick to edit
  isDNLTick?: boolean; // Whether the tick to delete is a DNL custom tick
}

// Utility functions for label formatting
const needsMathRendering = (label: string, numberType: string): boolean => {
  if (numberType !== 'fraction') {
    return false;
  }
  // Check if label contains a fraction (either as "a/b" or already as LaTeX "\frac{...}")
  return label.includes('/') || label.includes('\\frac');
};

const formatForMath = (label: string, numberType: string): string => {
  // If the label already contains LaTeX commands, return as-is to avoid double processing
  if (label.includes('\\frac') || label.includes('\\times') || label.includes('\\cdot')) {
    return label;
  }
  
  if (numberType === 'fraction' && label.includes('/')) {
    // Trim whitespace from the label
    const trimmedLabel = label.trim();
    
    // Handle negative fractions properly
    if (trimmedLabel.startsWith('-')) {
      // Negative fraction - put the minus sign outside the LaTeX fraction
      const positiveLabel = trimmedLabel.substring(1).trim(); // Remove the minus sign
      if (positiveLabel.includes(' ')) {
        // Mixed number like "-1 1/2"
        const parts = positiveLabel.split(/\s+/);
        const whole = parts[0].trim();
        const fraction = parts.slice(1).join(' ').trim();
        const [numerator, denominator] = fraction.split('/').map(s => s.trim());
        if (numerator && denominator) {
          return `-${whole}\\frac{${numerator}}{${denominator}}`;
        }
      } else {
        // Simple fraction like "-3/4"
        const [numerator, denominator] = positiveLabel.split('/').map(s => s.trim());
        if (numerator && denominator) {
          return `-\\frac{${numerator}}{${denominator}}`;
        }
      }
    } else {
      // Positive fraction
      if (trimmedLabel.includes(' ')) {
        // Mixed number like "1 1/2"
        const parts = trimmedLabel.split(/\s+/);
        const whole = parts[0].trim();
        const fraction = parts.slice(1).join(' ').trim();
        const [numerator, denominator] = fraction.split('/').map(s => s.trim());
        if (numerator && denominator) {
          return `${whole}\\frac{${numerator}}{${denominator}}`;
        }
      } else {
        // Simple fraction like "3/4"
        const [numerator, denominator] = trimmedLabel.split('/').map(s => s.trim());
        if (numerator && denominator) {
          return `\\frac{${numerator}}{${denominator}}`;
        }
      }
    }
  }
  return label;
};

const cleanDisplayText = (label: string, numberType: string): string => {
  if (numberType === 'percent') {
    // Clean up percentage formatting - remove unnecessary .0
    return label.replace(/\.0+%$/, '%');
  }
  return label;
};

/**
 * Format jump span value according to number type and preferences
 */
const formatJumpSpan = (
  span: number,
  numberType: string,
  constantsPrecision: number,
  locale: LocaleCode,
  fractionDisplay?: { format: 'mixed' | 'improper'; simplify: boolean }
): string => {
  switch (numberType) {
    case 'fraction':
      // For fractions, use the approximation logic to create LaTeX strings directly
      if (Math.abs(span) < 1e-10) {
        return '0';
      }
      try {
        const maxDen = fractionDisplay?.simplify !== false ? 100 : 1000; // Use higher denominator if not simplifying
        const fraction = approximate(span, maxDen);
        const mixed = fractionDisplay?.format === 'mixed';
        return formatFractionForMath(fraction, { mixed });
      } catch {
        // Fallback to decimal if fraction conversion fails
        return formatNumberWithPrecision(span, locale, constantsPrecision);
      }

    case 'decimal':
    default:
      // For decimals, use constants precision
      return formatNumberWithPrecision(span, locale, constantsPrecision);
  }
};

/**
 * Get tick styling based on hierarchical level
 * Level 0 = primary ticks, Level 1 = halves, Level 2 = quarters, Level 3+ = finer subdivisions
 * Now uses theme-aware colors from themeColors.ts
 */
interface TickStyle {
  height: number;
  strokeWidth: number;
  color: string;
}

// Legacy wrapper - components should use getThemeTickStyle with isDark parameter directly
const getTickStyleByLevel = (level: number, isOrigin: boolean = false, isCustom: boolean = false, isDark: boolean = false): TickStyle => {
  return getThemeTickStyle(level, isOrigin, isCustom, isDark);
};

/**
 * Props for the NumberLine component
 */
export interface NumberLineProps {
  /** Width of the number line viewport in pixels */
  width: number;
  /** Height of the number line viewport in pixels */
  height: number;
  /** Optional className for styling */
  className?: string;
  /** Optional ID for the SVG element */
  id?: string;
  /** Optional ref for export functionality */
  exportRef?: React.RefObject<HTMLDivElement>;
  /** Optional equivalent fractions configuration for stacked mode */
  equivalentFractions?: {
    denominator: number;
    integerLabelsOnly?: boolean;
    labelFormat?: 'fraction' | 'decimal';
  };
  /** Optional alignment guides (values) to render as vertical dotted lines */
  alignmentGuides?: number[];
  /** Optional label placement override (horizontal only) */
  labelPlacement?: 'above' | 'below';
  /** Visible width of the viewport (for centering calculations) */
  viewWidth?: number;
  /** Visible height of the viewport (for centering calculations) */
  viewHeight?: number;
}

/**
 * NumberLine Component
 * 
 * @example
 * <NumberLine width={800} height={140} />
 */
export const NumberLine: React.FC<NumberLineProps> = ({
  width,
  height,
  className = '',
  id = 'number-line',
  exportRef,
  equivalentFractions,
  alignmentGuides,
  labelPlacement = 'below',
  viewWidth,
  viewHeight,
}) => {
  const dispatch = useDispatch();
  const svgRef = useRef<SVGSVGElement>(null);
  const containerDivRef = useRef<HTMLDivElement>(null);
  
  // 1. FIRST: All Redux selectors and refs
  const toolMode = useSelector((state: RootState) => state.toolMode);
  const viewport = useSelector((state: RootState) => state.viewport);
  const numberLine = useSelector((state: RootState) => state.numberLines);
  const labelVisibility = useSelector((state: RootState) => state.labelVisibility);
  const customTicks = useSelector((state: RootState) => state.customTicks);
  const preferences = useSelector((state: RootState) => state.preferences);
  const locale = useSelector(selectLocale);
  const preferenceSubdivisionMode = useAppSelector(selectSubdivisionMode);
  const sessionSubdivisionMode = useAppSelector(state => state.ui.tickSubdivisionMode);
  const subdivisionMode = sessionSubdivisionMode ?? preferenceSubdivisionMode;
  const constants = useAppSelector(state => state.constants);
  const secondaryLine = useAppSelector(state => state.secondaryLine);
  const multipliers = useSelector((state: RootState) => state.multipliers);
  const jumpIndicators = useAppSelector(state => state.jumpIndicators);
  const ui = useAppSelector(state => state.ui);
  const activeTool = useAppSelector(selectActiveTool);
  const expressionObjects = useSelector(selectExpressionObjects);
  const measurement = useAppSelector(state => state.ui.measurement);
  const constantsPrecision = useAppSelector(selectConstantsPrecision);
  // Jump drawing state
  const isDrawingJump = useSelector(selectJumpDrawingMode);
  const defaultJumpLine = useSelector(selectDefaultJumpLine);
  const drawingJumpIdsRef = useRef<{ id: string; startValue: number }[]>([]);

  // DNL: Select DNL tools state
  const dnlTools = useAppSelector(state => state.dnlTools);
  const sparseModeEnabled = useAppSelector(state => state.dnlTools.sparseMode.enabled);
  const showZeroInSparse = useAppSelector(state => state.dnlTools.sparseMode.showZero);
  
  // Dark mode support
  const isDark = preferences.theme === 'dark';
  const themeColors = useMemo(() => getThemeColors(isDark), [isDark]);
  
  // Apply tool-specific classes
  const toolModeClass = `tool-mode-${toolMode.activeTool}`;
  const combinedClassName = `${className} ${toolModeClass}`;
  
  // 2. FIRST: All state declarations
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, offsetX: 0, offsetY: 0 });
  const [lastTouchDistance, setLastTouchDistance] = useState<number | null>(null);
  const [contextMenu, setContextMenu] = useState<ContextMenuState>({
    visible: false,
    position: { x: 0, y: 0 },
    mode: 'add-midpoint',
    intervalValues: undefined,
    tickToDelete: undefined,
    tickToEdit: undefined,
  });
  const [lastRightClickPosition, setLastRightClickPosition] = useState<{ x: number; y: number; value: number } | null>(null);
  const [dragState, setDragState] = useState<DragState>(initialDragState);
  const [dragPreview] = useState<any>(null);
  const [currentSnapResult] = useState<any>(null);
  // DNL: Get add point dialog state from Redux
  const addPointDialog = ui.addPointDialog || {
    isOpen: false,
    targetLine: 'primary' as const,
    initialValue: undefined,
  };
  
  // T-13: Jump drag state and refs
  const dragStateRef = useRef<DragState>(initialDragState);
  const dragPreviewRef = useRef<any>(null);
  
  // T-15: Store event listener functions in refs to avoid recreation issues
  const eventListenersRef = useRef<{
    mouseMove: ((e: MouseEvent) => void) | null;
    mouseUp: ((e: MouseEvent) => void) | null;
    touchMove: ((e: TouchEvent) => void) | null;
    touchEnd: ((e: TouchEvent) => void) | null;
  }>({
    mouseMove: null,
    mouseUp: null,
    touchMove: null,
    touchEnd: null
  });

  // Throttle pan dispatch to animation frames for smoother interaction
  const panRafIdRef = useRef<number | null>(null);
  const lastPanDeltaRef = useRef<number>(0);

  // 3. NEXT: All memoized values (useMemo)
  const isHorizontal = viewport.orientation === 'horizontal';
  const lineLength = isHorizontal ? width : height;
  // Use offsetX for horizontal, offsetY for vertical
  const activeOffset = isHorizontal ? viewport.offsetX : (viewport.offsetY ?? viewport.offsetX);
  const domain = useMemo(() => 
    computeVisibleDomain(activeOffset, viewport.scale, lineLength), 
    [viewport.scale, activeOffset, lineLength]
  );

  /**
   * Convert pixel position back to number line value
   */
  const pxToValueHelper = useCallback((px: number): number => {
    const adjustedPx = isHorizontal ? px : lineLength - px;
    return pxToValue(adjustedPx, domain, lineLength);
  }, [domain.start, domain.end, lineLength, isHorizontal]);
  
  // Remove baseDomain; secondary line uses the live domain so it pans/zooms identically to primary
  
  const customTicksInDomain = useMemo(() => {
    return selectCustomTicksInDomain(
      customTicks,
      domain,
      equivalentFractions?.denominator
    );
  }, [
    customTicks.ticks.length,
    customTicks.ticks,
    domain.start,
    domain.end,
    equivalentFractions?.denominator
  ]);

  const equivalentModeTicks = useMemo((): Tick[] | null => {
    if (!equivalentFractions) return null;
    const denom = Math.abs(Math.trunc(equivalentFractions.denominator));
    if (!denom || !Number.isFinite(denom)) return [];
    const showLabels = equivalentFractions.integerLabelsOnly !== false;
    const labelFormat = equivalentFractions.labelFormat ?? 'fraction';
    const decimalPlaces = Math.max(
      0,
      Math.min(preferences?.constantsPrecision ?? 2, 6)
    );

    const startIndex = Math.floor(domain.start * denom);
    const endIndex = Math.ceil(domain.end * denom);
    const maxTicks = 2400;
    if (endIndex - startIndex > maxTicks) {
      return [];
    }

    const ticks: Tick[] = [];
    for (let i = startIndex; i <= endIndex; i++) {
      const value = i / denom;
      const isWhole = i % denom === 0;
      const numerator = i;
      const label =
        showLabels
          ? labelFormat === 'decimal'
            ? value.toLocaleString(undefined, { maximumFractionDigits: decimalPlaces, minimumFractionDigits: 0 })
            : `${numerator}/${denom}`
          : undefined;
      ticks.push({
        value,
        major: isWhole,
        label,
        level: isWhole ? 0 : 1,
        showLabel: showLabels,
        source: 'generated',
      });
    }
    // Include custom ticks (midpoints/expressions)
    customTicksInDomain.forEach((t) => {
      const approxNumerator = Math.round(t.value * denom);
      const label =
        t.label ??
        (labelFormat === 'decimal'
          ? (t.value).toLocaleString(undefined, { maximumFractionDigits: decimalPlaces, minimumFractionDigits: 0 })
          : `${approxNumerator}/${denom}`);
      ticks.push({
        value: t.value,
        major: true,
        label: showLabels ? label : undefined,
        level: 0,
        showLabel: showLabels,
        source: t.type === 'midpoint' ? 'midpoint' : 'custom',
        customTick: t,
      });
    });
    return ticks;
  }, [equivalentFractions?.denominator, equivalentFractions?.integerLabelsOnly, equivalentFractions?.labelFormat, domain.start, domain.end, customTicksInDomain]);
  
  const ticks = useMemo(() => {
    if (equivalentFractions) {
      if (numberLine.tickMode === 'none') return [];
      return equivalentModeTicks ?? [];
    }

    if (numberLine.tickMode === 'none') return [];
    
    let effectiveNumberType = numberLine.numberType;
    let forceIntegerLabels = false;
    
    if (numberLine.numberType === 'integer' && 
        (numberLine.unitInterval.n !== 1 || numberLine.unitInterval.d !== 1)) {
      effectiveNumberType = 'decimal';
      forceIntegerLabels = false;
    }
    
    return generateTicks(
      domain,
      numberLine.unitInterval,
      viewport.scale,
      lineLength,
      {
        fractionDisplay: numberLine.fractionDisplay,
        numberType: effectiveNumberType,
        numberFormat: numberLine.numberFormat, // Phase 3: Pass numberFormat
        forceIntegerLabels,
        customTicks: customTicksInDomain,
        showSubticks: true,
        minPxSubtick: 8,
        maxSubdivisions: 4,
        subdivisionMode,
      },
      locale
    );
  }, [
    domain.start,
    domain.end,
    numberLine.unitInterval.n,
    numberLine.unitInterval.d,
    numberLine.fractionDisplay.format,
    numberLine.fractionDisplay.simplify,
    numberLine.numberType,
    numberLine.numberFormat, // Phase 3: Include numberFormat in dependencies
    numberLine.tickMode,
    viewport.scale,
    lineLength,
    customTicksInDomain,
    locale,
    subdivisionMode,
    equivalentFractions,
    equivalentModeTicks,
  ]);

  const majorTicks = useMemo(() => 
    ticks.filter(t => t.major).sort((a, b) => a.value - b.value),
    [ticks]
  );

  // Removed legacy secondary ticks generation (replaced by SecondaryLineLayer)

  const snapTargets = useMemo(() => {
    const targets: number[] = [];
    
    if (ui.dragSnap?.snapToTicks) {
      targets.push(...ticks.map(tick => tick.value));
    }
    
    if (ui.dragSnap?.snapToCustomTicks) {
      targets.push(...customTicks.ticks.map(tick => tick.value));
    }
    
    return [...new Set(targets)].sort((a, b) => a - b);
  }, [
    ticks,
    customTicks.ticks,
    ui.dragSnap?.snapToTicks,
    ui.dragSnap?.snapToCustomTicks
  ]);

  // T-15: State for snap feedback
  // const [currentSnapResult, setCurrentSnapResult] = useState<any>(null); // REMOVED: Duplicate declaration

  // 4. CRITICAL: All useCallback definitions BEFORE any useEffect that uses them
  
  // Helper function to get mouse/touch position relative to SVG
  const getRelativePosition = useCallback((clientX: number): number => {
    if (!svgRef.current) return 0;
    const rect = svgRef.current.getBoundingClientRect();
    const scaleX = width / rect.width;
    return (clientX - rect.left) * scaleX;
  }, [width]);

  // Helper function for vertical orientation
  const getRelativePositionY = useCallback((clientY: number): number => {
    if (!svgRef.current) return 0;
    const rect = svgRef.current.getBoundingClientRect();
    const scaleY = height / rect.height;
    return (clientY - rect.top) * scaleY;
  }, [height]);

  // Helper function to get touch distance (for pinch zoom)
  const getTouchDistance = useCallback((touches: React.TouchList | TouchList): number => {
    if (touches.length < 2) return 0;
    const touch1 = touches[0];
    const touch2 = touches[1];
    const dx = touch1.clientX - touch2.clientX;
    const dy = touch1.clientY - touch2.clientY;
    return Math.sqrt(dx * dx + dy * dy);
  }, []);

  // FIXED: Properly memoize value-to-pixel conversion
  const valueToPixel = useCallback((value: number): number => {
    // For vertical mode, invert the axis so values increase upward
    const basePx = valueToPx(value, domain, lineLength);
    return isHorizontal ? basePx : lineLength - basePx;
  }, [domain.start, domain.end, lineLength, isHorizontal]);

  // =============================================================================
  // LINE COORDINATE CALCULATIONS - FIXED TO WORK WITH SECONDARY LINE
  // =============================================================================

  // Calculate line position based on orientation
  // T-25 FIX: Push line down in equivalent fractions mode to provide headroom for jumps
  // We leave 45px bottom padding for tick labels, using the rest for jumps above
  const lineY = isHorizontal && equivalentFractions 
    ? height - 45 
    : height / 2;
  const lineX = width / 2;
  const labelsAboveLine = labelPlacement === 'above' && isHorizontal;
  // When labels are above (integer helper line), lift them but keep within view
  // T-25: Compact mode adjustments for equivalent fractions
  const isCompact = !!equivalentFractions;
  const compactLabelOffset = 22; // Reduced from 35 to fit in 45px bottom padding

  const minorTapOffset = labelsAboveLine ? -34 : 20;
  const minorLabelOffset = labelsAboveLine ? -28 : (isCompact ? 18 : 25);
  const minorPlaceholderOffset = labelsAboveLine ? -26 : 30;
  const majorTapOffset = labelsAboveLine ? -38 : 15;
  const majorLabelHitOffset = labelsAboveLine ? -42 : 30;
  const majorLabelOffset = labelsAboveLine ? -32 : (isCompact ? compactLabelOffset : 35);
  const majorPlaceholderOffset = labelsAboveLine ? -34 : 45;
  const secondaryLinePosition = useMemo(() => ({
    y: isHorizontal ? lineY - 120 : lineY, // Keep in sync with SecondaryLineLayer
    x: isHorizontal ? lineX : lineX + 140,
  }), [isHorizontal, lineX, lineY]);

  // Helper to safely get fraction simplify setting (supports both new and legacy format)
  const shouldSimplifyFraction = useMemo(() => {
    return secondaryLine.numberFormat?.fractionSimplify ?? 
           secondaryLine.fractionDisplay?.simplify ?? 
           true; // default to true
  }, [secondaryLine.numberFormat?.fractionSimplify, secondaryLine.fractionDisplay?.simplify]);

  const multiplierFraction = useMemo(() => {
    return shouldSimplifyFraction
      ? simplifyFraction(secondaryLine.scaleFactor)
      : secondaryLine.scaleFactor;
  }, [
    secondaryLine.scaleFactor.n,
    secondaryLine.scaleFactor.d,
    shouldSimplifyFraction
  ]);

  // Helper to safely get number type (supports both new and legacy format)
  const secondaryNumberType = useMemo(() => {
    if (secondaryLine.numberFormat?.type) {
      // Map new format types to legacy types
      const typeMap: Record<string, 'integer' | 'decimal' | 'fraction' | 'percent'> = {
        'integer': 'integer',
        'decimal': 'decimal',
        'fraction': 'fraction',
        'percentage': 'percent',
        'currency': 'decimal',
        'custom': 'integer',
      };
      return typeMap[secondaryLine.numberFormat.type] || 'fraction';
    }
    return secondaryLine.numberType || 'fraction';
  }, [secondaryLine.numberFormat?.type, secondaryLine.numberType]);

  // Helper to safely get fraction format (supports both new and legacy format)
  const fractionFormat = useMemo(() => {
    return secondaryLine.numberFormat?.fractionFormat ?? 
           secondaryLine.fractionDisplay?.format ?? 
           'mixed'; // default to mixed
  }, [secondaryLine.numberFormat?.fractionFormat, secondaryLine.fractionDisplay?.format]);

  const multiplierLabelData = useMemo(() => {
    const decimalValue = fractionToDecimal(multiplierFraction);

    // Helper to strip trailing zeros from fixed decimals
    const stripTrailingZeros = (value: string) => value.replace(/(\.\d*?[1-9])0+$/u, '$1').replace(/\.0+$/u, '');

    // Text + LaTeX based on secondary line number type
    switch (secondaryNumberType) {
      case 'decimal': {
        const textVal = decimalValue.toLocaleString(undefined, {
          maximumFractionDigits: 6,
          minimumFractionDigits: 0,
        });
        const latexVal = stripTrailingZeros(decimalValue.toFixed(6));
        return {
          text: `× ${textVal}`,
          latexSymbol: '\\times',
          latexValue: latexVal,
        };
      }
      case 'percent': {
        const percentVal = decimalValue * 100;
        const textVal = percentVal.toLocaleString(undefined, {
          maximumFractionDigits: 4,
          minimumFractionDigits: 0,
        });
        const latexVal = stripTrailingZeros(percentVal.toFixed(4));
        return {
          text: `× ${textVal}%`,
          latexSymbol: '\\times',
          latexValue: `${latexVal}\\%`,
        };
      }
      case 'integer': {
        const rounded = Math.round(decimalValue);
        const textVal = Number.isInteger(rounded) ? rounded.toString() : decimalValue.toLocaleString(undefined, { maximumFractionDigits: 6 });
        const latexVal = Number.isInteger(rounded)
          ? rounded.toString()
          : stripTrailingZeros(decimalValue.toFixed(6));
        return {
          text: `× ${textVal}`,
          latexSymbol: '\\times',
          latexValue: latexVal,
        };
      }
      case 'fraction':
      default: {
        return {
          text: `× ${formatScaleFactor(
            multiplierFraction,
            fractionFormat
          )}`,
          latexSymbol: '\\times',
          latexValue: fractionToLatex(
            multiplierFraction,
            fractionFormat
          ),
        };
      }
    }
  }, [
    multiplierFraction.n,
    multiplierFraction.d,
    fractionFormat,
    secondaryNumberType
  ]);

  // Line boundaries set to full viewport extents for edge-to-edge rendering
  const baseLineStart = 0;
  const baseLineEnd = lineLength;

  // FIXED: Calculate visible ticks and line boundaries together
  // Also filter by level visibility based on zoom level
  const lineRenderData = useMemo(() => {
    // Calculate primary interval spacing to determine which levels should be visible
    const majorTicks = ticks.filter(t => t.major && t.level === 0).sort((a, b) => a.value - b.value);
    let primarySpacingPx = 100; // default
    if (majorTicks.length >= 2) {
      const interval = Math.abs(majorTicks[1].value - majorTicks[0].value);
      primarySpacingPx = interval * viewport.scale;
    }
    
    // Get mode-specific spacing thresholds (must match ticks.ts)
    const getLevelMinSpacing = (mode: string): number[] => {
      switch (mode) {
        case 'decimal':
          return [50, 12, 12, 12, 8];
        case 'binary':
        case 'smart':
        default:
          return [50, 50, 25, 15, 10];
        case 'halves-tenths':
          return [50, 50, 25, 12, 8];
      }
    };
    const LEVEL_MIN_SPACING = getLevelMinSpacing(subdivisionMode);
    
    // Get subdivision factors based on current mode (must match ticks.ts)
    const subdivisionFactorsByMode: Record<string, number[]> = {
      smart: [2, 2, 2, 2],
      decimal: [10, 10, 10, 10],
      binary: [2, 2, 2, 2],
      'halves-tenths': [2, 2, 10, 10],
    };
    const subdivisionFactors = subdivisionFactorsByMode[subdivisionMode] || subdivisionFactorsByMode.smart;
    
    // Calculate which levels are visible based on spacing
    let currentSpacing = primarySpacingPx;
    const levelSpacings = [primarySpacingPx];
    for (const factor of subdivisionFactors) {
      currentSpacing = currentSpacing / factor;
      levelSpacings.push(currentSpacing);
    }
    
    const isLevelVisible = (level: number) => {
      const spacing = levelSpacings[level] ?? levelSpacings[levelSpacings.length - 1];
      const minRequired = LEVEL_MIN_SPACING[level] ?? 10;
      return spacing >= minRequired;
    };
    
    // Filter ticks that are visible within the viewport AND whose level is visible
    const visibleTicks = ticks.filter(tick => {
      const pos = valueToPixel(tick.value);
      const inBounds = pos >= baseLineStart && pos <= baseLineEnd;
      // Custom/midpoint ticks always visible, others check level
      const levelOk = tick.source === 'custom' || tick.source === 'midpoint' || isLevelVisible(tick.level);
      
      // DNL: In sparse mode, hide regular ticks unless they match a custom tick or are zero
      if (sparseModeEnabled && tick.source !== 'custom' && tick.source !== 'midpoint') {
        // Always show zero if configured
        if (showZeroInSparse && Math.abs(tick.value) < 1e-10) {
          return inBounds && levelOk;
        }
        // Check if this value matches any DNL custom tick
        const matchesDNLTick = dnlTools.sparseMode.customTicks.some(
          dnlTick => Math.abs(dnlTick.value - tick.value) < 1e-10 && 
                     (dnlTick.line === 'primary' || dnlTick.line === 'both') &&
                     dnlTick.showTick
        );
        if (!matchesDNLTick) {
          return false; // Hide this tick in sparse mode
        }
      }
      
      return inBounds && levelOk;
    });
    
    const majorVisible = visibleTicks.filter(tick => tick.major);
    const minorVisible = visibleTicks.filter(tick => !tick.major);
    
    // Calculate final line boundaries
    let finalLineStart = baseLineStart;
    let finalLineEnd = baseLineEnd;
    
    // Render the primary line exactly edge-to-edge of the viewport
    // Ticks may extend beyond but are clipped by the SVG viewport
    
    // Calculate line coordinates based on orientation
    const lineStartX = isHorizontal ? finalLineStart : lineX;
    const lineStartY = isHorizontal ? lineY : finalLineStart;
    const lineEndX = isHorizontal ? finalLineEnd : lineX;
    const lineEndY = isHorizontal ? lineY : finalLineEnd;
    
    return {
      majorVisibleTicks: majorVisible,
      minorVisibleTicks: minorVisible,
      lineStart: finalLineStart,
      lineEnd: finalLineEnd,
      lineStartX,
      lineStartY,
      lineEndX,
      lineEndY,
    };
  }, [
    ticks,
    lineLength,
    valueToPixel,
    isHorizontal,
    lineX,
    lineY,
    baseLineStart,
    baseLineEnd,
    viewport.scale,
    subdivisionMode,
    sparseModeEnabled,
    showZeroInSparse,
    dnlTools.sparseMode.customTicks,
  ]);

  // Extract the calculated values
  const {
    majorVisibleTicks,
    minorVisibleTicks,
    lineStartX,
    lineStartY,
    lineEndX,
    lineEndY,
  } = lineRenderData;

  const allVisibleTicks = useMemo(() => {
    return [...majorVisibleTicks, ...minorVisibleTicks];
  }, [majorVisibleTicks, minorVisibleTicks]);
  
  // T-15: Initialize snap hook
  const { calculateSnap } = useDragSnap({
    snapTargets,
    snapThreshold: ui.dragSnap.threshold,
    pixelToValue: (px: number) => pxToValueHelper(px),
    valueToPixel,
    enabled: ui.dragSnap.enabled
  });

  // Mouse Pan Handlers - ENHANCED for orientation
  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    // Handle Jump Drawing
    if (activeTool === 'jump' && isDrawingJump && e.button === 0) {
      // Check targets to avoid drawing on top of controls
      if (
        (e.target as Element).closest('.tick-label-container') ||
        (e.target as Element).closest('.jump-handle') ||
        (e.target as Element).closest('.jump-path') ||
        (e.target as Element).closest('.zero-jump-indicator')
      ) {
        return;
      }

      const clientPos = isHorizontal 
        ? getRelativePosition(e.clientX)
        : getRelativePositionY(e.clientY);
      
      const rawValue = pxToValueHelper(clientPos);
      const { snappedValue } = calculateSnap(rawValue);
      
      const jumps: { id: string; startValue: number }[] = [];
      const primaryId = nanoid();
      
      if (defaultJumpLine === 'primary' || defaultJumpLine === 'both') {
         jumps.push({ id: primaryId, startValue: snappedValue });
         dispatch(addJump({
           id: primaryId,
           startValue: snappedValue,
           endValue: snappedValue,
           line: 'primary'
         }));
      }
      
      if (defaultJumpLine === 'secondary' || defaultJumpLine === 'both') {
         // If both, we create a second jump with a new ID
         const secondaryId = defaultJumpLine === 'both' ? nanoid() : primaryId;
         jumps.push({ id: secondaryId, startValue: snappedValue });
         
         dispatch(addJump({
           id: secondaryId,
           startValue: snappedValue,
           endValue: snappedValue,
           line: 'secondary'
         }));
      }
      
      drawingJumpIdsRef.current = jumps;
      
      setIsDragging(true);
      e.preventDefault();
      e.stopPropagation();
      return;
    }

    // Allow panning for pan tool and jump tool (but not other tools)
    if (activeTool !== 'pan' && activeTool !== 'jump') {
      return;
    }
    
    // Only handle left mouse button and prevent if clicking on labels or jump handles
    if (e.button !== 0 || 
        (e.target as Element).closest('.tick-label-container') ||
        (e.target as Element).closest('.jump-handle') ||
        (e.target as Element).closest('.jump-path') ||
        (e.target as Element).closest('.zero-jump-indicator')) {
      return;
    }
    
    const clientPos = isHorizontal 
      ? getRelativePosition(e.clientX)
      : getRelativePositionY(e.clientY);
    setIsDragging(true);
    setDragStart({ x: clientPos, offsetX: viewport.offsetX, offsetY: viewport.offsetY ?? 0 });
    e.preventDefault();
  }, [getRelativePosition, getRelativePositionY, viewport.offsetX, viewport.offsetY, isHorizontal, activeTool, isDrawingJump, defaultJumpLine, pxToValueHelper, calculateSnap, dispatch]);

  const handleMouseMove = (e: MouseEvent) => {
    // Handle Jump Drawing
    if (drawingJumpIdsRef.current.length > 0) {
      const clientPos = isHorizontal 
        ? getRelativePosition(e.clientX)
        : getRelativePositionY(e.clientY);
      
      const rawValue = pxToValueHelper(clientPos);
      const { snappedValue } = calculateSnap(rawValue);
      
      drawingJumpIdsRef.current.forEach(jump => {
         dispatch(updateJumpEndpoints({
           id: jump.id,
           startValue: jump.startValue,
           endValue: snappedValue
         }));
      });
      e.preventDefault();
      return;
    }

    // Allow panning for pan tool and jump tool (but not other tools)
    if (activeTool !== 'pan' && activeTool !== 'jump') {
      return;
    }
    
    if (!isDragging) return;
    
    const clientPos = isHorizontal 
      ? getRelativePosition(e.clientX)
      : getRelativePositionY(e.clientY);
    
    // Calculate delta based on orientation and apply optional reverse preference
    const baseDelta = clientPos - dragStart.x;
    const dragDirectionFactor = preferences.wheelReverseScroll ? -1 : 1;
    const rawDelta = baseDelta * dragDirectionFactor;
    
    if (isHorizontal) {
      // Horizontal mode: pan along X axis
      const newOffsetX = dragStart.offsetX + rawDelta;
      const delta = newOffsetX - viewport.offsetX;
      lastPanDeltaRef.current = delta;
      if (panRafIdRef.current === null) {
        panRafIdRef.current = requestAnimationFrame(() => {
          dispatch(pan({ deltaX: lastPanDeltaRef.current }));
          panRafIdRef.current = null;
        });
      }
    } else {
      // Vertical mode: pan along Y axis (invert so moving up drags line up)
      const newOffsetY = dragStart.offsetY - rawDelta;
      const delta = newOffsetY - (viewport.offsetY ?? 0);
      lastPanDeltaRef.current = delta;
      if (panRafIdRef.current === null) {
        panRafIdRef.current = requestAnimationFrame(() => {
          dispatch(pan({ deltaY: lastPanDeltaRef.current }));
          panRafIdRef.current = null;
        });
      }
    }
    e.preventDefault();
  };

  const handleMouseUp = () => {
    if (drawingJumpIdsRef.current.length > 0) {
      drawingJumpIdsRef.current = [];
      setIsDragging(false);
      return;
    }
    setIsDragging(false);
  };

  // Wheel handler: Ctrl+wheel zooms, otherwise wheel pans (supports horizontal wheels/touchpads)
  const handleWheel = useCallback((e: WheelEvent) => {
    // Ctrl/⌘ + wheel → zoom at cursor
    if (e.ctrlKey) {
      e.preventDefault();
      e.stopPropagation();
      const mousePosX = getRelativePosition(e.clientX);
      const mousePosY = getRelativePositionY(e.clientY);
      const mousePos = isHorizontal ? mousePosX : mousePosY;
      const zoomDirection = e.deltaY < 0 ? 1.1 : 1 / 1.1;
      const newScale = viewport.scale * zoomDirection;
      const anchorValue = pxToValueHelper(mousePos);
      
      if (isHorizontal) {
        dispatch(zoomAt({
          scale: newScale,
          anchorX: mousePos + viewport.offsetX - anchorValue * viewport.scale,
        }));
      } else {
        dispatch(zoomAt({
          scale: newScale,
          anchorX: mousePosX, // Still need anchorX for the action signature
          anchorY: mousePos + (viewport.offsetY ?? 0) - anchorValue * viewport.scale,
        }));
      }
      return;
    }

    // No Ctrl key → pan using wheel deltas
    // - For horizontal orientation: use deltaX (trackpad horizontal swipe or horizontal wheel)
    // - For vertical orientation: use deltaY (natural vertical scroll when vertical)
    const wheelDelta = isHorizontal ? e.deltaX : e.deltaY;
    if (wheelDelta === 0) return;

    // Prevent the page/container from scrolling
    e.preventDefault();
    e.stopPropagation();

    // Apply reverse toggle and sensitivity from preferences.
    // Use the same preference for both orientations so vertical also respects the setting.
    const reverseFactor = preferences.wheelReverseScroll ? -1 : 1;
    const sensitivity = preferences.wheelScrollSensitivity ?? 1;
    // Baseline sign: scroll up (negative deltaY) moves the line up; reverseFactor flips it.
    const delta = -reverseFactor * wheelDelta * sensitivity;

    // Accumulate within the same animation frame for smoothness
    lastPanDeltaRef.current += delta;
    if (panRafIdRef.current === null) {
      panRafIdRef.current = requestAnimationFrame(() => {
        if (isHorizontal) {
          dispatch(pan({ deltaX: lastPanDeltaRef.current }));
        } else {
          dispatch(pan({ deltaY: lastPanDeltaRef.current }));
        }
        lastPanDeltaRef.current = 0;
        panRafIdRef.current = null;
      });
    }
  }, [dispatch, domain, getRelativePosition, getRelativePositionY, isHorizontal, lineLength, viewport.offsetX, viewport.offsetY, viewport.scale, preferences.wheelReverseScroll, preferences.wheelScrollSensitivity]);

  // Touch Handlers for Mobile - ENHANCED for orientation
  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    // Allow panning for pan tool and jump tool (but not other tools)
    if (activeTool !== 'pan' && activeTool !== 'jump') {
      return;
    }
    
    if (e.touches.length === 1) {
      // Single touch - start pan
      const touch = e.touches[0];
      const clientPos = isHorizontal 
        ? getRelativePosition(touch.clientX)
        : getRelativePositionY(touch.clientY);
      setIsDragging(true);
      setDragStart({ x: clientPos, offsetX: viewport.offsetX, offsetY: viewport.offsetY ?? 0 });
      setLastTouchDistance(null);
    } else if (e.touches.length === 2) {
      // Two touches - start pinch zoom
      setIsDragging(false);
      setLastTouchDistance(getTouchDistance(e.touches));
    }
    e.preventDefault();
  }, [getRelativePosition, getRelativePositionY, viewport.offsetX, viewport.offsetY, isHorizontal, activeTool, getTouchDistance]);

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    // Allow panning for pan tool and jump tool (but not other tools)
    if (activeTool !== 'pan' && activeTool !== 'jump') {
      return;
    }
    
    if (e.touches.length === 1 && isDragging) {
      // Single touch - pan
      const touch = e.touches[0];
    const clientPos = isHorizontal 
      ? getRelativePosition(touch.clientX)
      : getRelativePositionY(touch.clientY);
    
    const baseDelta = clientPos - dragStart.x;
    const dragDirectionFactor = preferences.wheelReverseScroll ? -1 : 1;
    const rawDelta = baseDelta * dragDirectionFactor;
      
      if (isHorizontal) {
        // Horizontal mode: pan along X axis
        const newOffsetX = dragStart.offsetX + rawDelta;
        const delta = newOffsetX - viewport.offsetX;
        lastPanDeltaRef.current = delta;
        if (panRafIdRef.current === null) {
          panRafIdRef.current = requestAnimationFrame(() => {
            dispatch(pan({ deltaX: lastPanDeltaRef.current }));
            panRafIdRef.current = null;
          });
        }
      } else {
        // Vertical mode: pan along Y axis; invert delta to match finger movement
        const newOffsetY = dragStart.offsetY - rawDelta;
        const delta = newOffsetY - (viewport.offsetY ?? 0);
        lastPanDeltaRef.current = delta;
        if (panRafIdRef.current === null) {
          panRafIdRef.current = requestAnimationFrame(() => {
            dispatch(pan({ deltaY: lastPanDeltaRef.current }));
            panRafIdRef.current = null;
          });
        }
      }
    } else if (e.touches.length === 2 && lastTouchDistance) {
      // Two touches - pinch zoom
      const currentDistance = getTouchDistance(e.touches);
      const zoomFactor = currentDistance / lastTouchDistance;
      
      // Calculate center point of pinch
      const touch1 = e.touches[0];
      const touch2 = e.touches[1];
      const centerPosX = getRelativePosition((touch1.clientX + touch2.clientX) / 2);
      const centerPosY = getRelativePositionY((touch1.clientY + touch2.clientY) / 2);
      const centerPos = isHorizontal ? centerPosX : centerPosY;
      
      const newScale = viewport.scale * zoomFactor;
      const anchorValue = pxToValueHelper(centerPos);
      
      if (isHorizontal) {
        dispatch(zoomAt({ 
          scale: newScale, 
          anchorX: centerPos + viewport.offsetX - anchorValue * viewport.scale 
        }));
      } else {
        dispatch(zoomAt({ 
          scale: newScale, 
          anchorX: centerPosX,
          anchorY: centerPos + (viewport.offsetY ?? 0) - anchorValue * viewport.scale 
        }));
      }
      
      setLastTouchDistance(currentDistance);
    }
    e.preventDefault();
  }, [isDragging, dragStart, lastTouchDistance, getRelativePosition, getRelativePositionY, viewport.scale, viewport.offsetX, viewport.offsetY, domain, lineLength, dispatch, isHorizontal, activeTool, getTouchDistance]);

  const handleTouchEnd = useCallback(() => {
    setIsDragging(false);
    setLastTouchDistance(null);
  }, []);

  // T-17: Enhanced right-click handler with expression support
  const handleContextMenu = useCallback((event: React.MouseEvent<SVGSVGElement>) => {
    // Skip if the event is from a DNL custom tick (handled by SparsePointsRenderer)
    const target = event.target as HTMLElement;
    if (target?.closest?.('.dnl-custom-tick, .sparse-tick')) {
      return; // Let SparsePointsRenderer handle it
    }
    
    event.preventDefault();
    
    const rect = svgRef.current?.getBoundingClientRect();
    if (!rect) return;

    // Get click position relative to SVG's displayed bounding rect
    const relativeX = event.clientX - rect.left;
    const relativeY = event.clientY - rect.top;
    
    // Scale to SVG internal coordinates (account for CSS scaling when SVG is smaller than natural size)
    const scaleX = width / rect.width;
    const scaleY = height / rect.height;
    const svgX = relativeX * scaleX;
    const svgY = relativeY * scaleY;
    
    // Convert to world value based on orientation using scaled coordinates
    const rawClickValue = pxToValueHelper(isHorizontal ? svgX : svgY);

    // Find the nearest tick value to snap to (if ticks are available)
    let clickValue = rawClickValue;
    if (ticks && ticks.length > 0) {
      try {
        const nearestTick = ticks.reduce((nearest, tick) => {
          const distance = Math.abs(tick.value - rawClickValue);
          const nearestDistance = Math.abs(nearest.value - rawClickValue);
          return distance < nearestDistance ? tick : nearest;
        }, ticks[0]);
        clickValue = nearestTick.value;
      } catch (error) {
        // Fallback to raw value if there's any error
        console.warn('Error finding nearest tick:', error);
        clickValue = rawClickValue;
      }
    }

    // Store click position and value for expression creation
    setLastRightClickPosition({ 
      x: event.clientX, 
      y: event.clientY, 
      value: clickValue 
    });

    // First, check if we clicked on a DNL custom tick (within tolerance)
    // Scale the tolerance to account for CSS scaling
    const clickTolerance = 10 * Math.max(scaleX, scaleY); // pixels, scaled
    
    // Check for DNL custom ticks (sparse points)
    const dnlTicksWithPositions = dnlTools.sparseMode.customTicks
      .filter(tick => tick.value >= domain.start && tick.value <= domain.end)
      .map(tick => ({
        ...tick,
        position: valueToPixel(tick.value)
      }));

    // Find DNL custom tick within click tolerance (using scaled SVG coordinates)
    const clickedDNLTick = dnlTicksWithPositions.find(tick => {
      const clickPos = isHorizontal ? svgX : svgY;
      const distance = Math.abs(clickPos - tick.position);
      return distance <= clickTolerance;
    });

    if (clickedDNLTick) {
      // Show delete menu for DNL custom tick
      setContextMenu({
        visible: true,
        position: { x: event.clientX, y: event.clientY },
        mode: 'delete-tick',
        tickToDelete: clickedDNLTick,
        isDNLTick: true,
      });
      return;
    }

    // Then check if we clicked on a regular custom tick (within tolerance)
    const customTicksWithPositions = customTicksInDomain.map(tick => ({
      ...tick,
      position: valueToPixel(tick.value)
    }));

    // Find custom tick within click tolerance (using scaled SVG coordinates)
    const clickedCustomTick = customTicksWithPositions.find(tick => {
      const clickPos = isHorizontal ? svgX : svgY;
      const distance = Math.abs(clickPos - tick.position);
      return distance <= clickTolerance;
    });

    if (clickedCustomTick) {
      // Show delete menu for custom tick
      setContextMenu({
        visible: true,
        position: { x: event.clientX, y: event.clientY },
        mode: 'delete-tick',
        tickToDelete: clickedCustomTick,
        isDNLTick: false,
      });
      return;
    }

    // If no custom tick clicked, check for adding midpoint between major ticks
    const majorTicks = ticks ? ticks.filter(tick => tick.major) : [];
    
    // Find the interval containing this click (use rawClickValue to detect intervals)
    let intervalStart: number | null = null;
    let intervalEnd: number | null = null;

    if (majorTicks.length >= 2) {
      for (let i = 0; i < majorTicks.length - 1; i++) {
        const startTick = majorTicks[i];
        const endTick = majorTicks[i + 1];
        
        // Use rawClickValue to determine which interval we're in
        if (rawClickValue >= startTick.value && rawClickValue <= endTick.value) {
          intervalStart = startTick.value;
          intervalEnd = endTick.value;
          break;
        }
      }
    }

    // Always show the context menu when right-clicking
    setContextMenu({
      visible: true,
      position: { x: event.clientX, y: event.clientY },
      mode: intervalStart !== null && intervalEnd !== null ? 'add-midpoint' : 'add-midpoint',
      intervalValues: intervalStart !== null && intervalEnd !== null 
        ? { start: intervalStart, end: intervalEnd }
        : undefined,
    });
  }, [ticks, valueToPixel, pxToValueHelper, isHorizontal, width, height, customTicksInDomain]);

  // Removed unused closeContextMenu helper

  // Removed unused handlers for midpoint and tick deletion

  // T-18: Add annotation styles
  const annotationStyles = `
    .annotation-canvas {
      pointer-events: auto;
    }
    
    .stroke-selected {
      filter: drop-shadow(0 0 3px #3B82F6);
    }
    
    .text-selected {
      background-color: rgba(59, 130, 246, 0.1) !important;
      border: 1px dashed #3B82F6 !important;
    }
    
    .shape-preview {
      pointer-events: none;
    }
    
    .stroke-preview {
      pointer-events: none;
    }
    
    .annotation-svg {
      overflow: visible;
    }
    
    .text-editor {
      font-family: system-ui, sans-serif;
    }
  `;

  // Annotation helpers removed (unused)

  // T-18: Update cursor style based on active tool
  const getCursorStyle = () => {
    const toolCursors: Record<string, string> = {
      select: 'default',
      pan: 'grab',
      text: 'text',
      equation: 'text',
      pen: 'crosshair',
      highlighter: 'crosshair',
      eraser: 'pointer',
      rectangle: 'crosshair',
      'rectangle-filled': 'crosshair',
      circle: 'crosshair',
      line: 'crosshair',
      jump: 'crosshair',
      measurement: 'crosshair',
    };
    return toolCursors[activeTool] || 'default';
  };

  /**
   * Handle jump selection
   */
  const handleJumpClick = useCallback((jumpId: string, event?: React.MouseEvent) => {
    const ctrlKey = event?.ctrlKey || event?.metaKey;
    dispatch(toggleJumpSelection({ id: jumpId, ctrlKey }));
  }, [dispatch]);

  /**
   * Handle background click to deselect all jumps and expressions
   */
  const handleBackgroundClick = useCallback((event: React.MouseEvent<SVGSVGElement>) => {
    // Treat clicks anywhere that are NOT on jump elements as background clicks.
    // Also guard against interfering while panning or measuring.
    const target = event.target as Element | null;
    const clickedOnJump = target?.closest?.('.jump-indicator, .jump-handle, .jump-path-click-area, .jump-path-visual');
    const clickedOnTick = target?.closest?.('.tick-label-container, .major-tick-group, .minor-tick-group, .label-click-area');
    const clickedOnDNLPoint = target?.closest?.('.dnl-custom-tick, .sparse-point');

    // DNL: Click handler removed - custom points now accessible via right-click menu or toolbar button

    if (!clickedOnJump && !isDragging && !measurement.isActive) {
      dispatch(deselectAllJumps());
      dispatch(deselectAllExpressionObjects());
      // T-16: Only clear measurements when not using measurement tool AND no measurements exist
      if (activeTool !== 'measurement' && measurement.measurements.length === 0) {
        dispatch(clearAllMeasurements());
      }
    }
  }, [activeTool, isDragging, measurement.isActive, dispatch]);

  // T-11: Helper function to calculate constant label dimensions
  const calculateConstantLabelDimensions = useCallback((constant: any, precision: number) => {
    const labelText = createConstantLabel(constant, precision);
    // More accurate width calculation: account for different character widths
    // Greek letters and numbers have different widths, so use a conservative estimate
    const estimatedWidth = Math.max(labelText.length * 9 + 24, 90); // 9px per char + 24px padding
    const estimatedHeight = 44; // Fixed height with adequate padding for 16px font
    return { width: estimatedWidth, height: estimatedHeight };
  }, []);

  // T-11: Constants rendering helper function
  const renderConstants = () => {
    // Get constants visible in current domain
    const visibleConstants = getVisibleConstants(
      domain,
      MATHEMATICAL_CONSTANTS.filter(c => constants.visibleConstants.includes(c.id))
    );

    if (visibleConstants.length === 0) return null;

    // Calculate positions with collision avoidance
    const positions = calculateConstantPositions(
      visibleConstants,
      valueToPixel,
      isHorizontal,
      lineX,
      lineY,
      20, // labelHeight
      50  // minSpacing
    );

    return (
      <g className="constants-overlay">
        {positions.map(({ constant, x, y, offsetY }) => {
          const markerSize = 6;
          const constantValue = constant.value();
          const markerPos = valueToPixel(constantValue);
          
          // Marker coordinates based on orientation
          const markerX = isHorizontal ? markerPos : lineX;
          const markerY = isHorizontal ? lineY : markerPos;
          
          // Label positioning
          const labelX = isHorizontal ? x : x + 10;
          const labelY = isHorizontal ? y : y + 4;
          
          // Calculate dynamic dimensions for the label box
          const { width: labelWidth, height: labelHeight } = calculateConstantLabelDimensions(constant, constantsPrecision);
          const labelOffsetX = labelWidth / 2;
          const labelOffsetY = labelHeight / 2;
          
          return (
            <g key={constant.id} className="constant-marker">
              {/* Constant marker */}
              <circle
                cx={markerX}
                cy={markerY}
                r={markerSize}
                fill={themeColors.constantMarker}
                stroke={themeColors.constantMarkerStroke}
                strokeWidth="2"
                className="constant-point"
              />
              
              {/* Connection line from marker to label */}
              {offsetY > 0 && (
                <line
                  x1={markerX}
                  y1={markerY}
                  x2={isHorizontal ? x : x}
                  y2={isHorizontal ? lineY - 20 - offsetY : y}
                  stroke={themeColors.constantLine}
                  strokeWidth="1"
                  strokeDasharray="2,2"
                  className="constant-connection"
                />
              )}
              
              {/* Label */}
              {constants.showLabels && (
                <foreignObject
                  x={labelX - labelOffsetX}
                  y={labelY - labelOffsetY}
                  width={labelWidth}
                  height={labelHeight}
                  className="constant-label"
                >
                  <div
                    style={{
                      fontSize: `${16 * textScale}px`,
                      color: themeColors.constantLabelText,
                      backgroundColor: isDark ? 'rgba(30, 58, 95, 0.95)' : 'rgba(255, 255, 255, 0.9)',
                      border: `1px solid ${themeColors.constantLabelBorder}`,
                      borderRadius: '4px',
                      padding: '6px 10px',
                      textAlign: 'center',
                      fontFamily: 'system-ui, sans-serif',
                      fontWeight: '500',
                      whiteSpace: 'nowrap',
                      boxShadow: isDark ? '0 1px 3px rgba(0, 0, 0, 0.3)' : '0 1px 3px rgba(0, 0, 0, 0.1)',
                      width: '100%',
                      height: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      lineHeight: '1.2'
                    }}
                    title={`${constant.name}: ${constantValue}`}
                  >
                    {createConstantLabel(constant, constantsPrecision)}
                  </div>
                </foreignObject>
              )}
            </g>
          );
        })}
      </g>
    );
  };

  // T-17: Render expression objects on the number line
  const renderExpressionObjects = () => {
    const expressions = useSelector(selectExpressionObjects);
    const expressionsEnabled = useSelector(selectExpressionsEnabled);
    
    if (!expressionsEnabled || expressions.length === 0) {
      return null;
    }

    // Filter to only show expressions with valid positions in current domain
    const lineDenominator = equivalentFractions?.denominator;
    const visibleExpressions = expressions.filter((expr: ExpressionObject) => 
      expr.position !== null &&
      expr.position >= domain.start &&
      expr.position <= domain.end &&
      (lineDenominator === undefined || expr.equivalentDenominator === lineDenominator)
    );

    return (
      <g className="expression-objects-overlay">
        {visibleExpressions.map((expr: ExpressionObject) => {
          const pixelPosition = valueToPixel(expr.position!);
          
          // Calculate position based on orientation
          const indicatorX = isHorizontal ? pixelPosition : lineX;
          const indicatorY = isHorizontal ? lineY : pixelPosition;

          return (
            <ExpressionIndicator
              key={expr.id}
              object={expr}
              x={indicatorX}
              y={indicatorY}
              isHorizontal={isHorizontal}
              isSelected={expr.isSelected}
            />
          );
        })}
      </g>
    );
  };



  /**
   * Format fraction label according to display settings
   */
  // Removed unused formatFractionLabel helper





  // T-25: Enhanced constants value formatting
  // Removed unused formatConstantValue helper

  // T-25: Update jump label display formatting (inlined where needed)

  

  /**
   * Handle secondary line label clicks without interfering with panning
   */
  const handleSecondaryLabelClick = useCallback((valueKey: string, event: React.MouseEvent) => {
    // Prevent this click from initiating a pan
    event.stopPropagation();
    event.preventDefault();
    
    // Don't process clicks if we're currently panning
    if (isDragging) {
      return;
    }
    
    const currentHidden = labelVisibility[valueKey]?.hidden;
    
    if (secondaryLine.hideAllLabels) {
      // When hide-all is active, clicking sets hidden=false to show
      const newHidden = currentHidden === false ? true : false;
      dispatch(setLabelVisibility({ valueKey, hidden: newHidden }));
    } else {
      // When showing all, clicking sets hidden=true to hide
      const newHidden = currentHidden === true ? false : true;
      dispatch(setLabelVisibility({ valueKey, hidden: newHidden }));
    }
  }, [dispatch, labelVisibility, secondaryLine.hideAllLabels, isDragging]);



  /**
   * T-25 NEW: Helper function to format jump labels with locale awareness
   */
  // Removed unused formatJumpLabelWithLocale
  // (intentionally left blank)

  /**
   * T-25 ENHANCED: Format jump span with locale support
   */
  // Removed unused formatJumpSpan
  // (intentionally left blank)

  /**
   * T-25 NEW: Enhanced TickLabel component with locale support
   */
  interface TickLabelProps {
    tick: any; // Using any for now to avoid type issues
    locale: LocaleCode;
    numberType: 'integer' | 'decimal' | 'fraction' | 'percent'; // Added numberType prop
    x?: number;
    y?: number;
    textAnchor?: string;
    dominantBaseline?: string;
    className?: string;
    style?: React.CSSProperties;
  }

  const TickLabel = ({
    tick,
    locale,
    numberType,
    x,
    y,
    textAnchor,
    dominantBaseline,
    className,
    style,
    ...otherProps
  }: TickLabelProps) => {
    // The tick label is already formatted with locale in generateTicks
    // This component just needs to render it properly
    
    // For mathematical rendering (fractions), use foreignObject to embed HTML/KaTeX in SVG
    const useMathRendering = needsMathRendering(tick.label || '', numberType);

    if (useMathRendering) {
      // Use fixed dimensions like secondary line does - more stable during panning
      const fontSize = style?.fontSize ? parseFloat(String(style.fontSize)) : 14;
      const width = 60;
      const height = 28;
      
      // Adjust position based on textAnchor
      let adjustedX = x || 0;
      if (textAnchor === 'middle') {
        adjustedX = (x || 0) - width / 2;
      } else if (textAnchor === 'end') {
        adjustedX = (x || 0) - width;
      }
      
      // Adjust Y position based on dominantBaseline
      let adjustedY = y || 0;
      if (dominantBaseline === 'hanging') {
        // Label hangs below the baseline point - small offset for visual balance
        adjustedY = (y || 0) - 2;
      } else if (dominantBaseline === 'middle') {
        adjustedY = (y || 0) - height / 2;
      } else {
        // Default: baseline at bottom
        adjustedY = (y || 0) - height + 4;
      }
      
      return (
        <foreignObject
          x={adjustedX}
          y={adjustedY}
          width={width}
          height={height}
          className={className}
          style={{ pointerEvents: 'none', overflow: 'visible' }}
        >
          <div 
            style={{ 
              display: 'flex',
              alignItems: 'center',
              justifyContent: textAnchor === 'middle' ? 'center' : textAnchor === 'end' ? 'flex-end' : 'flex-start',
              width: '100%',
              height: '100%',
              fontSize: style?.fontSize,
              fontWeight: style?.fontWeight,
              color: style?.fill as string || 'currentColor',
              pointerEvents: 'none',
              userSelect: 'none',
            }}
          >
            <MathRenderer 
              expression={formatForMath(tick.label || '', numberType)}
            />
          </div>
        </foreignObject>
      );
    }
    
    // For simple text labels, display as-is (already locale-formatted)
    return (
      <text x={x} y={y} textAnchor={textAnchor} dominantBaseline={dominantBaseline} className={className} style={style} {...otherProps}>
        {cleanDisplayText(tick.label || '', numberType)}
      </text>
    );
  };










  const calculateLabelPositions = (jumpsWithLanes: Array<Jump & { lane?: number }>) => {
    // T-25: Compact mode for equivalent fractions to fit in small vertical space
    const isCompact = !!equivalentFractions;
    
    // Use smaller offsets in compact mode
    const jumpLineOffset = isCompact ? 25 : 40;
    const laneSpacing = isCompact ? 25 : 45;
    
    // Label offset: 25px for compact, 45px for fractions (to avoid overlap), 28px standard
    // T-25 FIX: Increased compact offset to 32px (was 25px) to prevent overlap with jump lines
    const labelYOffset = isCompact 
      ? 32 
      : (numberLine.fractionDisplay.format === 'mixed' || numberLine.fractionDisplay.format === 'improper' ? 45 : 28);

    return jumpsWithLanes.map(jump => {
      const lane = jump.lane ?? 0;
      const startPx = valueToPixel(jump.startValue);
      const endPx = valueToPixel(jump.endValue);
      
      const jumpY = isHorizontal 
        ? lineY - jumpLineOffset - getLaneOffset(lane, laneSpacing)
        : lineX + 40 + getLaneOffset(lane, 45);
      
      const labelX = isHorizontal 
        ? (startPx + endPx) / 2 
        : jumpY + 20;
      
      const labelY = isHorizontal 
        ? jumpY - labelYOffset - (lane * 5)
        : (valueToPixel(jump.startValue) + valueToPixel(jump.endValue)) / 2;

      return {
        jump,
        lane,
        jumpY,
        labelX,
        labelY,
        startPx,
        endPx
      };
    });
  };


  const createJumpPathOnly = (startX: number, endX: number, y: number, height: number = 30): string => {
    const width = Math.abs(endX - startX);
    const midX = (startX + endX) / 2;
    const controlY = y - height;
    
    let path = `M ${startX} ${y}`;
    
    if (width > 20) {
      path += ` Q ${midX} ${controlY} ${endX} ${y}`;
    } else {
      path += ` L ${endX} ${y}`;
    }
    
    return path;
  };

  const createVerticalJumpPathOnly = (x: number, startY: number, endY: number, width: number): string => {
    const height = Math.abs(endY - startY);
    const midY = (startY + endY) / 2;
    const controlX = x + width;
    
    let path = `M ${x} ${startY}`;
    
    if (height > 20) {
      path += ` Q ${controlX} ${midY} ${x} ${endY}`;
    } else {
      path += ` L ${x} ${endY}`;
    }
    
    return path;
  };

  // 5. LAST: All useEffect hooks that use the callbacks
  
  // Keep refs in sync with state
  useEffect(() => {
    dragStateRef.current = dragState;
  }, [dragState]);
  
  useEffect(() => {
    dragPreviewRef.current = dragPreview;
  }, [dragPreview]);

  // Set up event listeners for drag and wheel
  useEffect(() => {
    const svgElement = svgRef.current;
    if (!svgElement) return;

    // Mouse drag listeners - always add them, they will only be active when isDragging is true
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    // CRITICAL: Native wheel listener with passive: false to prevent browser zoom
    svgElement.addEventListener('wheel', handleWheel, { passive: false });

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      svgElement.removeEventListener('wheel', handleWheel);
    };
  }, [handleMouseMove, handleMouseUp, handleWheel]);

  // One-time migration: clear stale secondary label overrides on first mount
  useEffect(() => {
    try {
      const MIGRATION_KEY = 'nl:migrated:clearSecondaryLabelOverrides:v1';
      const done = localStorage.getItem(MIGRATION_KEY);
      if (!done) {
        dispatch(clearSecondaryLabelOverrides());
        localStorage.setItem(MIGRATION_KEY, 'true');
      }
    } catch (e) {
      // ignore storage errors silently
    }
  }, [dispatch]);

  // T-16: Keyboard event handling for delete key
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Delete' || event.key === 'Backspace') {
        // Delete selected measurements
        const selectedMeasurements = measurement.measurements.filter(m => m.selected);
        if (selectedMeasurements.length > 0) {
          dispatch(deleteSelectedMeasurements());
          event.preventDefault();
        }
        
        // Delete selected jumps
        const selectedJumps = jumpIndicators.jumps.filter(j => j.selected);
        if (selectedJumps.length > 0) {
          dispatch(deleteSelectedJumps());
          event.preventDefault();
        }

        // Delete selected expression objects
        const selectedExpressions = expressionObjects.filter((obj: ExpressionObject) => obj.isSelected);
        if (selectedExpressions.length > 0) {
          dispatch(deleteSelectedExpressionObjects());
          event.preventDefault();
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [dispatch, measurement.measurements, jumpIndicators.jumps, expressionObjects]);

  // Add these temporarily for debugging
  useEffect(() => {
    console.log('🔍 Redux State Update - labelVisibility:', labelVisibility);
  }, [labelVisibility]);

  useEffect(() => {
    console.log('🔍 Redux State Update - secondaryLine.hideAllLabels:', secondaryLine.hideAllLabels);
  }, [secondaryLine.hideAllLabels]);

  // MOVED: These useEffect hooks will be placed after all useCallback functions




  // Removed legacy StableSecondaryLine component (superseded by SecondaryLineLayer)

  // Remove the old renderSecondaryLine useCallback since we're using the stable component
  // const renderSecondaryLine = useCallback(() => { ... }, [ ... ]);

  // Determine if we should show the origin marker and calculate its position precisely
  // Use the same valueToPixel function that ticks use to ensure perfect alignment
  const showOrigin = domain.start <= 0 && domain.end >= 0;
  const originPos = showOrigin ? valueToPixel(0) : 0;
  // Keep the marker on the axis in both orientations; shift only the label in vertical mode
  const originX = isHorizontal ? originPos : lineX;
  const originY = isHorizontal ? lineY : originPos;
    
  // T-10: Calculate midpoint value for context menu (only for add mode) - FIX: Round to avoid floating point errors
  const midpointValue = contextMenu.mode === 'add-midpoint' && contextMenu.intervalValues
    ? Math.round(((contextMenu.intervalValues.start + contextMenu.intervalValues.end) / 2) * 1e10) / 1e10
    : undefined;

  const addMultiplierMarker = useCallback((value: number) => {
    if (!secondaryLine.enabled) return;

    // Snap to nearest visible tick (major/minor/custom/midpoint)
    const snappedValue = allVisibleTicks.length
      ? allVisibleTicks.reduce((closest, tick) => {
          const currentDiff = Math.abs(tick.value - value);
          const bestDiff = Math.abs(closest.value - value);
          return currentDiff < bestDiff ? tick : closest;
        }).value
      : value;

    dispatch(addMultiplierMarkerAction(snappedValue));
  }, [secondaryLine.enabled, allVisibleTicks, dispatch]);

  const clearMultiplierMarkers = useCallback(() => {
    dispatch(clearMultiplierMarkersAction());
  }, [dispatch]);
    
  const handleMeasurementMouseDown = useCallback((event: React.MouseEvent) => {
    if (activeTool !== 'measurement') return;
    
    const clientPos = isHorizontal 
      ? getRelativePosition(event.clientX)
      : getRelativePositionY(event.clientY);
    
    const rawValue = pxToValueHelper(clientPos);
    
    // Apply snap logic
    // Default to snapping, disable if Alt key is held
    let snapResult;
    if (event.altKey) {
      snapResult = {
        snappedValue: rawValue,
        isSnapping: false,
        snapTarget: null,
        snapDistance: 0
      };
    } else {
      snapResult = calculateSnap(rawValue);
    }

    dispatch(startMeasurement({ 
      x: clientPos,
      value: snapResult.snappedValue,
      isSnapped: snapResult.isSnapping,
      snappedValue: snapResult.snappedValue
    }));
  }, [activeTool, isHorizontal, getRelativePosition, getRelativePositionY, domain, lineLength, pxToValueHelper, calculateSnap, dispatch]);

  const handleDocumentMouseMove: MouseMoveHandler = useCallback((e: MouseEvent) => {
    eventListenersRef.current.mouseMove?.(e);
  }, []);

  const handleDocumentMouseUp: MouseUpHandler = useCallback((e: MouseEvent) => {
    eventListenersRef.current.mouseUp?.(e);
  }, []);

  const handleDocumentTouchMove: TouchMoveHandler = useCallback((e: TouchEvent) => {
    eventListenersRef.current.touchMove?.(e);
  }, []);

  const handleDocumentTouchEnd: TouchEndHandler = useCallback(() => {
    eventListenersRef.current.touchEnd?.();
  }, []);

  // T-13: Jump drag start handler
  const handleJumpDragStart = useCallback((
    event: React.MouseEvent | React.TouchEvent,
    jumpId: string,
    handleType: 'start' | 'end' | 'whole'
  ) => {
    event.preventDefault();
    event.stopPropagation();
    
    const jump = jumpIndicators.jumps.find(j => j.id === jumpId);
    if (!jump || !svgRef.current) return;
    
    const pos = getEventPosition(event.nativeEvent as any);
    
    const startValue = handleType === 'start' ? jump.startValue : 
                      handleType === 'end' ? jump.endValue : 
                      jump.startValue;
    
    const newDragState = {
      isDragging: false,
      jumpId,
      handleType,
      startMousePos: pos,
      startValue,
      originalStartValue: jump.startValue,
      originalEndValue: jump.endValue,
      dragOffset: { x: 0, y: 0 }
    };
    
    setDragState(newDragState);
    dragStateRef.current = newDragState;
    
    document.addEventListener('mousemove', handleDocumentMouseMove);
    document.addEventListener('mouseup', handleDocumentMouseUp);
    document.addEventListener('touchmove', handleDocumentTouchMove);
    document.addEventListener('touchend', handleDocumentTouchEnd);
  }, [jumpIndicators.jumps]);

  // Visibility helpers (backward compatible): prefer namespaced keys, fallback to legacy
  const mainLabelsHidden = useAppSelector(state => state.ui.mainLabelsHidden ?? false);
  const textScale = useAppSelector(state => state.preferences.textScale ?? 1);

  const buildPrimaryLabelKey = useCallback(
    (valueKey: string) =>
      equivalentFractions
        ? `eq:${equivalentFractions.denominator}:${valueKey}`
        : `primary-${valueKey}`,
    [equivalentFractions]
  );

  const getPrimaryHidden = useCallback(
    (valueKey: string): boolean | undefined => {
      const namespaced = labelVisibility[buildPrimaryLabelKey(valueKey)]?.hidden;
      if (typeof namespaced === 'boolean') return namespaced;
      const legacyPrimary = labelVisibility[`primary-${valueKey}`]?.hidden;
      if (typeof legacyPrimary === 'boolean') return legacyPrimary;
      return labelVisibility[valueKey]?.hidden; // legacy fallback
    },
    [labelVisibility, buildPrimaryLabelKey]
  );

  // No separate helper for secondary; read namespaced keys directly where used

  // Helper function to handle primary label clicks
  // Always toggles the *visible* state, respecting the current global toggle.
  const handleLabelClick = useCallback(
    (value: number) => {
      const baseKey = value.toString();
      const namespacedKey = buildPrimaryLabelKey(baseKey);

      // Base visibility ignores the global toggle and only looks at per‑label overrides.
      const currentHiddenOverride = getPrimaryHidden(baseKey);
      const baseVisible = currentHiddenOverride !== true;

      // Actual visibility depends on the global "toggle labels" state.
      const currentlyVisible = mainLabelsHidden ? !baseVisible : baseVisible;

      // We want to flip the *actual* visibility.
      // Derive the new hidden flag so that, after this change,
      // the label renders as the opposite of its current visible state.
      const newHidden = mainLabelsHidden ? !currentlyVisible : currentlyVisible;

      dispatch(setLabelVisibility({ valueKey: namespacedKey, hidden: newHidden }));
    },
    [dispatch, getPrimaryHidden, mainLabelsHidden, buildPrimaryLabelKey]
  );

  const canvasDimensions = useMemo(() => ({ width, height }), [width, height]);

  const createArrowheadPath = useCallback((x: number, y: number, pointsRight: boolean, isHorizontal: boolean) => {
    const size = 15;
    const arrowWidth = 12;
    if (isHorizontal) {
      return pointsRight
        ? `M ${x} ${y} L ${x - size} ${y - arrowWidth} L ${x - size} ${y + arrowWidth} Z`
        : `M ${x} ${y} L ${x + size} ${y - arrowWidth} L ${x + size} ${y + arrowWidth} Z`;
    } else {
      // For vertical: pointsRight means endValue > startValue, which means pointing UP (toward lower Y)
      // In SVG, lower Y is up, so "points up" = toward lower Y values
      return pointsRight
        ? `M ${x} ${y} L ${x - arrowWidth} ${y + size} L ${x + arrowWidth} ${y + size} Z` // Points UP
        : `M ${x} ${y} L ${x - arrowWidth} ${y - size} L ${x + arrowWidth} ${y - size} Z`; // Points DOWN
    }
  }, []);

  // T-13: Enhanced jump rendering with drag support
  const renderJumpIndicators = useCallback(() => {
    if (jumpIndicators.jumps.length === 0 && !dragPreview) return null;

    // Remove suppression of jump labels in equivalent fraction mode
    // Jump labels should be visible unless explicitly toggled off on the jump itself
    // const suppressJumpLabels = equivalentFractions ? mainLabelsHidden : false;
    const suppressJumpLabels = false;

    // Combine actual jumps with drag preview
    let jumpsToRender = [...jumpIndicators.jumps];
    
    if (dragPreview) {
      jumpsToRender = jumpsToRender.map(jump => 
        jump.id === dragPreview.id ? dragPreview : jump
      );
    }
    
    // Calculate lanes for collision avoidance
    const jumpsWithLanes = calculateJumpLanes(jumpsToRender, valueToPixel, 40);
    const jumpPositions = calculateLabelPositions(jumpsWithLanes);
    
    return (
      <g className="jump-indicators">
        {jumpPositions.map(({ jump, jumpY, startPx, endPx, labelX, labelY }) => {
          const jumpWidth = Math.abs(endPx - startPx);
          const isZeroLength = Math.abs(jump.endValue - jump.startValue) < 0.001;
          const isSmallJump = jumpWidth < 30;
          const arrowHeight = isSmallJump ? 20 : 30;
          const isBeingDragged = dragStateRef.current.jumpId === jump.id && dragStateRef.current.isDragging;
          const isDragPreview = (jump as any).isDragPreview;
          
          const pointsRight = jump.endValue > jump.startValue;
          let arrowheadTransform = '';

          if (isHorizontal) {
            const halfDx = (endPx - startPx) / 2;
            if (Math.abs(halfDx) > 0.1) {
              const angleRad = Math.atan2(arrowHeight, halfDx);
              let angleDeg = angleRad * (180 / Math.PI);
              if (!pointsRight) {
                angleDeg -= 180;
              }
              arrowheadTransform = `rotate(${angleDeg} ${endPx} ${jumpY})`;
            }
          } else {
            // Vertical mode: calculate rotation to align arrow with the curve's tangent at the end
            const startY = valueToPixel(jump.startValue);
            const endY = valueToPixel(jump.endValue);
            const arcWidth = arrowHeight; // The horizontal extent of the arc
            
            // Calculate the tangent angle at the end of the quadratic curve
            // The curve goes from (jumpY, startY) through control point (jumpY + arcWidth, midY) to (jumpY, endY)
            // For a quadratic bezier, the tangent at the end is from control point to end point
            const midY = (startY + endY) / 2;
            const controlX = jumpY + arcWidth;
            
            // Vector from control point to end point
            const dx = jumpY - controlX; // dx is negative (going left back to line)
            const dy = endY - midY;
            
            // Calculate angle and apply rotation
            const angleRad = Math.atan2(dy, dx);
            const angleDeg = angleRad * (180 / Math.PI);
            arrowheadTransform = `rotate(${angleDeg} ${jumpY} ${endY})`;
          }

          return (
            <g 
              key={jump.id} 
              className={`jump-indicator ${jump.selected ? 'selected' : ''} ${isBeingDragged ? 'dragging' : ''}`}
              style={{ opacity: isDragPreview ? 0.8 : 1 }}
            >
              
              {/* Selection highlight */}
              {jump.selected && (
                <rect
                  x={isHorizontal ? Math.min(startPx, endPx) - 8 : jumpY - arrowHeight - 8}
                  y={isHorizontal ? jumpY - arrowHeight - 8 : Math.min(valueToPixel(jump.startValue), valueToPixel(jump.endValue)) - 8}
                  width={isHorizontal ? jumpWidth + 16 : arrowHeight * 2 + 16}
                  height={isHorizontal ? arrowHeight + 16 : jumpWidth + 16}
                  fill="none"
                  stroke="#3b82f6"
                  strokeWidth="2"
                  strokeDasharray="4,4"
                  className="selection-outline"
                  rx="6"
                />
              )}
              
              {/* Invisible larger click area for jump path */}
              <path
                d={isHorizontal 
                  ? createJumpPathOnly(startPx, endPx, jumpY, arrowHeight)
                  : createVerticalJumpPathOnly(jumpY, valueToPixel(jump.startValue), valueToPixel(jump.endValue), arrowHeight)
                }
                stroke="transparent"
                strokeWidth="12"
                fill="none"
                strokeLinecap="round"
                strokeLinejoin="round"
                style={{ 
                  cursor: isBeingDragged ? 'grabbing' : 'pointer',
                  pointerEvents: 'auto'
                }}
                onClick={(e) => !isBeingDragged && handleJumpClick(jump.id, e)}
                onMouseDown={(e) => {
                  const rect = e.currentTarget.getBoundingClientRect();
                  const startHandleX = Math.abs(startPx - (e.clientX - rect.left));
                  const endHandleX = Math.abs(endPx - (e.clientX - rect.left));
                  
                  if (startHandleX > 25 && endHandleX > 25) {
                    e.preventDefault();
                    e.stopPropagation();
                    handleJumpDragStart(e, jump.id, 'whole');
                  }
                }}
                className="jump-path-click-area"
              />
              
              {/* Visible jump path */}
              <path
                d={isHorizontal 
                  ? createJumpPathOnly(startPx, endPx, jumpY, arrowHeight)
                  : createVerticalJumpPathOnly(jumpY, valueToPixel(jump.startValue), valueToPixel(jump.endValue), arrowHeight)
                }
                stroke={jump.color}
                strokeWidth={jump.selected ? 4 : 3}
                fill="none"
                strokeLinecap="round"
                strokeLinejoin="round"
                style={{ 
                  cursor: isBeingDragged ? 'grabbing' : 'pointer',
                  strokeDasharray: isDragPreview ? '4,4' : 'none',
                  pointerEvents: 'none'
                }}
                className="jump-path-visual"
                markerEnd={jump.endValue > jump.startValue ? 'url(#arrowhead-green)' : 'url(#arrowhead-red)'}
              />
              
              {/* Jump label */}
              {jump.showLabel && !suppressJumpLabels && (() => {
                const jumpNumberType = equivalentFractions
                  ? equivalentFractions.labelFormat === 'decimal'
                    ? 'decimal'
                    : 'fraction'
                  : numberLine.numberType;
                // Calculate the jump span (difference between start and end values)
                const jumpSpan = Math.abs(jump.endValue - jump.startValue);
                // Format the span properly according to number type and constants precision
                const spanText = formatJumpSpan(jumpSpan, jumpNumberType, constantsPrecision, locale, numberLine.fractionDisplay);
                // Check if the formatted text already contains LaTeX (for fractions) or needs conversion
                const hasLatex = spanText.includes('\\');
                const labelExpression = hasLatex ? spanText : formatForMath(spanText, jumpNumberType);
                const renderMath = hasLatex || needsMathRendering(spanText, jumpNumberType);
                return renderMath ? (
                  <foreignObject
                    x={labelX - 40}
                    y={labelY - 40}
                    width="80"
                    height="80"
                    style={{ pointerEvents: 'none', overflow: 'visible' }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: '100%',
                        height: '100%',
                        fontSize: `${14 * textScale}px`,
                        fontWeight: 'bold',
                        color: jump.color,
                        pointerEvents: 'none',
                        userSelect: 'none',
                      }}
                    >
                      <MathRenderer expression={labelExpression} />
                    </div>
                  </foreignObject>
                ) : (
                  <text
                    x={labelX}
                    y={labelY}
                    fill={jump.color}
                    textAnchor="middle"
                    dominantBaseline="middle"
                    className="jump-label"
                    style={{
                      fontSize: `${14 * textScale}px`,
                      fontWeight: 'bold',
                      pointerEvents: 'none',
                      userSelect: 'none'
                    }}
                  >
                    {cleanDisplayText(spanText, jumpNumberType)}
                  </text>
                );
              })()}
              
              {/* Jump handles */}
              <circle
                cx={isHorizontal ? startPx : jumpY}
                cy={isHorizontal ? jumpY : valueToPixel(jump.startValue)}
                r={isBeingDragged && dragStateRef.current.handleType === 'start' ? 10 : 8}
                fill={jump.color}
                stroke="white"
                strokeWidth="2"
                style={{ 
                  cursor: getDragCursor(isBeingDragged && dragStateRef.current.handleType === 'start', 'start', true),
                  filter: isBeingDragged && dragStateRef.current.handleType === 'start' ? 'drop-shadow(0 2px 4px rgba(0,0,0,0.2))' : 'none'
                }}
                className="jump-handle start-handle"
                onMouseDown={(e) => handleJumpDragStart(e, jump.id, 'start')}
                onTouchStart={(e) => handleJumpDragStart(e, jump.id, 'start')}
                onClick={(e) => {
                  e.stopPropagation();
                  if (!isBeingDragged) handleJumpClick(jump.id, e);
                }}
              />
              
              {!isZeroLength && (
                <path
                  d={isHorizontal 
                    ? createArrowheadPath(endPx, jumpY, jump.endValue > jump.startValue, isHorizontal)
                    : createArrowheadPath(jumpY, valueToPixel(jump.endValue), jump.endValue > jump.startValue, isHorizontal)
                  }
                  transform={arrowheadTransform}
                  fill={jump.color}
                  stroke="white"
                  strokeWidth="2"
                  style={{ 
                    cursor: getDragCursor(isBeingDragged && dragStateRef.current.handleType === 'end', 'end', true),
                    filter: isBeingDragged && dragStateRef.current.handleType === 'end' ? 'drop-shadow(0 2px 4px rgba(0,0,0,0.2))' : 'none'
                  }}
                  className="jump-handle end-handle"
                  onMouseDown={(e) => handleJumpDragStart(e, jump.id, 'end')}
                  onTouchStart={(e) => handleJumpDragStart(e, jump.id, 'end')}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (!isBeingDragged) handleJumpClick(jump.id, e);
                  }}
                />
              )}
            </g>
          );
        })}
      </g>
    );
  }, [
    jumpIndicators.jumps,
    dragPreview,
    valueToPixel,
    isHorizontal,
    lineY,
    lineX,
    handleJumpClick,
    handleJumpDragStart,
    createArrowheadPath,
    canvasDimensions,
    mainLabelsHidden,
    equivalentFractions,
    numberLine,
    textScale
  ]);

  // Helper functions for jump rendering


  // =============================================================================
  // USEEFFECT HOOKS - PLACED AFTER ALL USECALLBACK FUNCTIONS
  // =============================================================================

  // T-16: Global mouse event handling for measurement tool
  useEffect(() => {
    if (activeTool === 'measurement') {
      document.addEventListener('mousemove', handleDocumentMouseMove);
      document.addEventListener('mouseup', handleDocumentMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleDocumentMouseMove);
      document.removeEventListener('mouseup', handleDocumentMouseUp);
    };
  }, [activeTool, handleDocumentMouseMove, handleDocumentMouseUp]);

  // Cleanup jump drag listeners when drag state changes
  useEffect(() => {
    return () => {
      // Clean up any remaining jump drag listeners
      document.removeEventListener('mousemove', handleDocumentMouseMove);
      document.removeEventListener('mouseup', handleDocumentMouseUp);
      document.removeEventListener('touchmove', handleDocumentTouchMove);
      document.removeEventListener('touchend', handleDocumentTouchEnd);
    };
  }, [handleDocumentMouseMove, handleDocumentMouseUp, handleDocumentTouchMove, handleDocumentTouchEnd]);

  useEffect(() => {
    eventListenersRef.current.mouseMove = (e: MouseEvent) => {
      // Handle jump dragging events
      if (dragStateRef.current.jumpId && !dragStateRef.current.isDragging) {
        const { startMousePos } = dragStateRef.current;
        const currentPos = getEventPosition(e);
        const distance = Math.sqrt(
          Math.pow(currentPos.x - startMousePos!.x, 2) +
            Math.pow(currentPos.y - startMousePos!.y, 2)
        );
  
        if (distance > 5) { // 5px drag threshold
          const updatedDragState = { ...dragStateRef.current, isDragging: true };
          setDragState(updatedDragState);
          dragStateRef.current = updatedDragState;
        }
      }
  
      // Handle measurement tool events
      if (measurement.isActive && activeTool === 'measurement') {
        const clientPos = isHorizontal
          ? getRelativePosition(e.clientX)
          : getRelativePositionY(e.clientY);
        
        const rawValue = pxToValueHelper(clientPos);
        
        // Apply snap logic - snap by default, disable if Alt is held
        let snapResult;
        if (e.altKey) {
          snapResult = {
            snappedValue: rawValue,
            isSnapping: false,
            snapTarget: null,
            snapDistance: 0
          };
        } else {
          snapResult = calculateSnap(rawValue);
        }

        dispatch(updateMeasurement({ 
          x: clientPos,
          value: snapResult.snappedValue,
          isSnapped: snapResult.isSnapping,
          snappedValue: snapResult.snappedValue
        }));
      }
      
      // Handle jump dragging events (once started)
      if (dragStateRef.current.isDragging && dragStateRef.current.jumpId && svgRef.current) {
        const clientPos = isHorizontal 
          ? getRelativePosition(e.clientX)
          : getRelativePositionY(e.clientY);
        
        const rawValue = pxToValueHelper(clientPos);
        
        // Apply snapping unless Alt key is held
        let snapResult;
        if (e.altKey) {
          snapResult = {
            snappedValue: rawValue,
            isSnapping: false,
            snapTarget: null,
            snapDistance: 0
          };
        } else {
          snapResult = calculateSnap(rawValue);
        }
        
        const newValue = snapResult.snappedValue;
        const jump = jumpIndicators.jumps.find(j => j.id === dragStateRef.current.jumpId);
        
        if (jump && dragStateRef.current.handleType) {
          let newEndpoints: { startValue: number; endValue: number };
          
          if (dragStateRef.current.handleType === 'whole' && 
              dragStateRef.current.originalStartValue !== null &&
              dragStateRef.current.originalEndValue !== null &&
              dragStateRef.current.startValue !== null) {
            // For 'whole' drags, calculate delta from where drag started and apply to original values
            const delta = newValue - dragStateRef.current.startValue;
            newEndpoints = {
              startValue: dragStateRef.current.originalStartValue + delta,
              endValue: dragStateRef.current.originalEndValue + delta
            };
          } else {
            // For 'start' and 'end' drags, use the standard calculation
            const originalJump = { startValue: jump.startValue, endValue: jump.endValue };
            newEndpoints = calculateDragEndpoints(
              originalJump,
              dragStateRef.current.handleType,
              newValue
            );
          }
          
          // Update the jump endpoints in real-time
          dispatch(updateJumpEndpoints({
            id: dragStateRef.current.jumpId,
            startValue: newEndpoints.startValue,
            endValue: newEndpoints.endValue
          }));
        }
      }
    };

    eventListenersRef.current.mouseUp = (e: MouseEvent) => {
      const wasDragging = dragStateRef.current.isDragging;
      const jumpId = dragStateRef.current.jumpId;

      // Always clean up drag state
      setDragState(initialDragState);
      dragStateRef.current = initialDragState;

      // If it wasn't a drag, treat it as a click for selection
      if (!wasDragging && jumpId) {
        const ctrlKey = e.ctrlKey || e.metaKey;
        dispatch(toggleJumpSelection({ id: jumpId, ctrlKey }));
      }

      // Handle measurement tool events
      if (measurement.isActive && activeTool === 'measurement') {
        // If we have a full start/end pair, complete; otherwise cancel the in‑progress measurement
        if (measurement.startPoint && measurement.endPoint) {
          const snapInfo = {
            isSnapped: measurement.startPoint.isSnapped || measurement.endPoint.isSnapped || false,
            snappedStartValue: measurement.startPoint.snappedValue,
            snappedEndValue: measurement.endPoint.snappedValue,
          };
          dispatch(completeMeasurement(snapInfo));
        } else {
          dispatch(cancelMeasurement());
        }
      }
    };

    eventListenersRef.current.touchMove = (e: TouchEvent) => {
      if (dragStateRef.current.jumpId && !dragStateRef.current.isDragging && e.touches.length === 1) {
        const { startMousePos } = dragStateRef.current;
        const currentPos = getEventPosition(e);
        const distance = Math.sqrt(
          Math.pow(currentPos.x - startMousePos!.x, 2) +
          Math.pow(currentPos.y - startMousePos!.y, 2)
        );
  
        if (distance > 5) { // 5px drag threshold
          const updatedDragState = { ...dragStateRef.current, isDragging: true };
          setDragState(updatedDragState);
          dragStateRef.current = updatedDragState;
        }
      }
      // Touch handling for measurement tool
      if (measurement.isActive && activeTool === 'measurement' && e.touches.length === 1) {
        const touch = e.touches[0];
        const clientPos = isHorizontal 
          ? getRelativePosition(touch.clientX)
          : getRelativePositionY(touch.clientY);
        
        const rawValue = pxToValueHelper(clientPos);
        const snapResult = calculateSnap(rawValue);
        
        dispatch(updateMeasurement({ 
          x: clientPos,
          value: snapResult.snappedValue,
          isSnapped: snapResult.isSnapping,
          snappedValue: snapResult.snappedValue
        }));
      }
      
      // Touch handling for jump dragging
      if (dragStateRef.current.isDragging && dragStateRef.current.jumpId && svgRef.current && e.touches.length === 1) {
        const touch = e.touches[0];
        const clientPos = isHorizontal 
          ? getRelativePosition(touch.clientX)
          : getRelativePositionY(touch.clientY);
        
        const rawValue = pxToValueHelper(clientPos);
        const snapResult = calculateSnap(rawValue);
        const newValue = snapResult.snappedValue;
        
        const jump = jumpIndicators.jumps.find(j => j.id === dragStateRef.current.jumpId);
        
        if (jump && dragStateRef.current.handleType) {
          const newEndpoints = calculateDragEndpoints(
            { startValue: jump.startValue, endValue: jump.endValue },
            dragStateRef.current.handleType,
            newValue
          );
          
          dispatch(updateJumpEndpoints({
            id: dragStateRef.current.jumpId,
            startValue: newEndpoints.startValue,
            endValue: newEndpoints.endValue
          }));
        }
      }
    };

    eventListenersRef.current.touchEnd = () => {
      const wasDragging = dragStateRef.current.isDragging;
      const jumpId = dragStateRef.current.jumpId;
  
      // Always clean up drag state
      setDragState(initialDragState);
      dragStateRef.current = initialDragState;
  
      // If it wasn't a drag, treat it as a click for selection
      if (!wasDragging && jumpId) {
        // For touch, there's no direct ctrlKey equivalent, so we don't support multi-select on tap
        dispatch(toggleJumpSelection({ id: jumpId, ctrlKey: false }));
      }
  
      // Handle measurement tool events
      if (measurement.isActive && activeTool === 'measurement') {
        if (measurement.startPoint && measurement.endPoint) {
          const snapInfo = {
            isSnapped: measurement.startPoint.isSnapped || measurement.endPoint.isSnapped || false,
            snappedStartValue: measurement.startPoint.snappedValue,
            snappedEndValue: measurement.endPoint.snappedValue,
          };
          dispatch(completeMeasurement(snapInfo));
        } else {
          dispatch(cancelMeasurement());
        }
      }
    };
  }, [
    measurement.isActive, 
    activeTool, 
    isHorizontal, 
    getRelativePosition, 
    getRelativePositionY, 
    domain, 
    lineLength, 
    pxToValueHelper, 
    calculateSnap, 
    dispatch, 
    jumpIndicators.jumps,
    measurement.startPoint,
    measurement.endPoint,
    handleDocumentMouseMove,
    handleDocumentMouseUp,
    handleDocumentTouchMove,
    handleDocumentTouchEnd
  ]);

  useEffect(() => {
    return () => {
      // Clean up any remaining jump drag listeners
      document.removeEventListener('mousemove', handleDocumentMouseMove);
      document.removeEventListener('mouseup', handleDocumentMouseUp);
      document.removeEventListener('touchmove', handleDocumentTouchMove);
      document.removeEventListener('touchend', handleDocumentTouchEnd);
    };
  }, [handleDocumentMouseMove, handleDocumentMouseUp, handleDocumentTouchMove, handleDocumentTouchEnd]);

  return (
    <>
      <div ref={exportRef} className={`number-line-container ${combinedClassName}`.trim()}>
        {/* Export-specific container that wraps only the SVG content */}
        <div className="number-line-export-area" style={{ position: 'relative', display: 'block', width: '100%' }}>
          <svg
            ref={svgRef}
            id={id}
            width={width}
            height={height}
            viewBox={`0 0 ${width} ${height}`}
            className="number-line-svg"
            style={{
              overflow: 'visible', // Allow labels to extend beyond SVG bounds
              userSelect: 'none',
              cursor: getCursorStyle(),
              touchAction: 'none', // Prevent default touch behaviors
              display: 'block', // CENTERING FIX: Prevent inline spacing issues
              maxWidth: '100%', // CENTERING FIX: Ensure responsive behavior
            }}
            onMouseDown={activeTool === 'measurement' ? handleMeasurementMouseDown : handleMouseDown}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
            onContextMenu={handleContextMenu} // T-10: Add context menu handler
            onClick={handleBackgroundClick} // T-15: Add background click handler
          >
            {/* Alignment guides (equivalent mode) */}
            {alignmentGuides && alignmentGuides.length > 0 && (
              alignmentGuides.map((val, idx) => {
                const pos = valueToPixel(val);
                return (
                  <line
                    key={`align-${idx}`}
                    x1={isHorizontal ? pos : 0}
                    y1={isHorizontal ? 0 : pos}
                    x2={isHorizontal ? pos : width}
                    y2={isHorizontal ? height : pos}
                    stroke={themeColors.alignmentLine}
                    strokeWidth={1}
                    strokeDasharray="2,3"
                    className="alignment-guide"
                  />
                );
              })
            )}

            {/* Main axis line */}
            <line
              x1={lineStartX}
              y1={lineStartY}
              x2={lineEndX}
              y2={lineEndY}
              stroke={themeColors.axis}
              strokeWidth="2"
              className="axis-line"
            />
            
            {/* Secondary line (aligned with primary via shared domain) - disabled in equivalent fractions mode */}
            {!equivalentFractions && (
              <SecondaryLineLayer
                enabled={secondaryLine.enabled}
                isHorizontal={isHorizontal}
                width={width}
                height={height}
                lineX={lineX}
                lineY={lineY}
                scale={viewport.scale}
                domain={domain}
                unitInterval={numberLine.unitInterval}
                scaleFactor={secondaryLine.scaleFactor}
                numberType={secondaryNumberType}
                fractionDisplay={{
                  format: fractionFormat,
                  simplify: shouldSimplifyFraction
                }}
                locale={locale}
                hideAllLabels={secondaryLine.hideAllLabels}
                labelVisibility={labelVisibility}
                onLabelClick={(valueKey, e) => handleSecondaryLabelClick(valueKey, e)}
                subdivisionMode={subdivisionMode}
              />
            )}

            {/* DNL Sparse Points - Primary Line */}
            {!equivalentFractions && (
              <SparsePointsRenderer
                line="primary"
                isHorizontal={isHorizontal}
                domain={domain}
                lineLength={lineLength}
                lineX={lineX}
                lineY={lineY}
                scale={viewport.scale}
                unitSuffix={dnlTools.primaryUnit}
                numberFormat={numberLine.numberFormat || { type: 'integer' }}
                labelPosition={numberLine.labelPosition}
                onTickContextMenu={(tickId, e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  const tick = dnlTools.sparseMode.customTicks.find(t => t.id === tickId);
                  if (tick) {
                    setContextMenu({
                      visible: true,
                      position: { x: e.clientX, y: e.clientY },
                      mode: 'edit-tick',
                      tickToEdit: tick,
                      tickToDelete: tick,
                      isDNLTick: true,
                    });
                  }
                }}
              />
            )}

            {/* DNL Sparse Points - Secondary Line */}
            {!equivalentFractions && secondaryLine.enabled && (
              <SparsePointsRenderer
                line="secondary"
                isHorizontal={isHorizontal}
                domain={domain}
                lineLength={lineLength}
                lineX={secondaryLinePosition.x}
                lineY={secondaryLinePosition.y}
                scale={viewport.scale}
                unitSuffix={dnlTools.secondaryUnit}
                numberFormat={secondaryLine.numberFormat}
                labelPosition="above"
                onTickContextMenu={(tickId, e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  const tick = dnlTools.sparseMode.customTicks.find(t => t.id === tickId);
                  if (tick) {
                    setContextMenu({
                      visible: true,
                      position: { x: e.clientX, y: e.clientY },
                      mode: 'edit-tick',
                      tickToEdit: tick,
                      tickToDelete: tick,
                      isDNLTick: true,
                    });
                  }
                }}
              />
            )}

            {/* DNL Line Labels */}
            {!equivalentFractions && (
              <LineLabelsRenderer
                isHorizontal={isHorizontal}
                primaryLinePos={isHorizontal ? lineY : lineX}
                secondaryLinePos={isHorizontal ? secondaryLinePosition.y : secondaryLinePosition.x}
                lineStart={viewWidth ? (width - viewWidth) / 2 + 20 : 20}
                width={width}
                height={height}
              />
            )}

            {/* DNL Mapping Arrows */}
            {!equivalentFractions && secondaryLine.enabled && dnlTools.mappingArrows.enabled && (
              <MappingArrowsLayer
                customTicks={dnlTools.sparseMode.customTicks}
                arrowsConfig={dnlTools.mappingArrows}
                isHorizontal={isHorizontal}
                primaryLineY={isHorizontal ? lineY : lineX}
                secondaryLineY={isHorizontal ? secondaryLinePosition.y : secondaryLinePosition.x}
                domain={domain}
                lineLength={lineLength}
                scaleFactor={secondaryLine.scaleFactor}
                isDark={preferences.theme === 'dark'}
              />
            )}

            {/* Multiplier markers: show mapping from primary to secondary at clicked value */}
            {!equivalentFractions && secondaryLine.enabled && multipliers.markers.length > 0 && (
              <g className="multiplier-markers" pointerEvents="none">
                {multipliers.markers.map((marker) => {
                  const axisPos = valueToPixel(marker.value);
                  const fontSize = 14 * textScale;
                  const valueKey = `multiplier-${marker.value}`;
                  const hiddenOverride = labelVisibility[valueKey]?.hidden;
                  const isLabelVisible = secondaryLine.hideAllLabels
                    ? hiddenOverride === false
                    : hiddenOverride !== true;
                  const toggleLabel = (e: React.MouseEvent | React.TouchEvent) => {
                    e.stopPropagation();
                    const newHidden = isLabelVisible;
                    dispatch(setLabelVisibility({ valueKey, hidden: newHidden }));
                  };
                  if (isHorizontal) {
                    const x = axisPos;
                    const yPrimary = lineY;
                    const ySecondary = secondaryLinePosition.y;
                    const midY = (yPrimary + ySecondary) / 2;
                    const labelHeight = 30;
                    const symbolWidth = 32;
                    const valueWidth = 70;
                    const symbolX = x - symbolWidth - 10; // keep × left of the arrow
                    const valueX = x + 12;               // move numeric value to the right
                    const labelY = midY - labelHeight / 2;
                    const arrowHead = `${x},${ySecondary} ${x - 7},${ySecondary + 14} ${x + 7},${ySecondary + 14}`;
                    return (
                      <g key={marker.id} className="multiplier-marker">
                        <line
                          x1={x}
                          y1={yPrimary}
                          x2={x}
                          y2={ySecondary}
                          stroke={themeColors.secondaryAxis}
                          strokeWidth={2}
                        />
                        <polygon
                          points={arrowHead}
                          fill={themeColors.secondaryAxis}
                        />
                        <foreignObject
                          x={symbolX}
                          y={labelY}
                          width={symbolWidth + 4}
                          height={labelHeight}
                          style={{ overflow: 'visible', pointerEvents: 'auto' }}
                          onClick={toggleLabel}
                          onMouseDown={(e) => e.stopPropagation()}
                          onTouchStart={(e) => e.stopPropagation()}
                        >
                          {isLabelVisible ? (
                            <div
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'flex-end',
                                fontSize: `${fontSize}px`,
                                fontWeight: 600,
                                color: themeColors.secondaryLabel,
                                cursor: 'pointer',
                                paddingRight: '4px',
                              }}
                            >
                              <MathRenderer expression={multiplierLabelData.latexSymbol} />
                            </div>
                          ) : (
                            <div style={{
                              width: '16px',
                              height: '16px',
                              borderRadius: '9999px',
                              border: `1px dashed ${themeColors.secondaryLabel}`,
                              background: 'transparent',
                              marginLeft: '8px',
                              cursor: 'pointer',
                            }} />
                          )}
                        </foreignObject>
                        {isLabelVisible && (
                          <foreignObject
                            x={valueX}
                            y={labelY}
                            width={valueWidth}
                            height={labelHeight}
                            style={{ overflow: 'visible', pointerEvents: 'auto' }}
                            onClick={toggleLabel}
                            onMouseDown={(e) => e.stopPropagation()}
                            onTouchStart={(e) => e.stopPropagation()}
                          >
                            <div
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'flex-start',
                                fontSize: `${fontSize}px`,
                                fontWeight: 600,
                                color: themeColors.secondaryLabel,
                                cursor: 'pointer',
                                paddingLeft: '4px',
                              }}
                            >
                              <MathRenderer expression={multiplierLabelData.latexValue} />
                            </div>
                          </foreignObject>
                        )}
                      </g>
                    );
                  } else {
                    const y = axisPos;
                    const xPrimary = lineX;
                    const xSecondary = secondaryLinePosition.x;
                    const midX = (xPrimary + xSecondary) / 2;
                    const labelHeight = 32;
                    const symbolWidth = 32;
                    const valueWidth = 70;
                    const gap = 8;
                    const totalWidth = symbolWidth + gap + valueWidth;
                    // Position centered between lines and above the arrow
                    const labelX = midX - totalWidth / 2;
                    const labelY = y - labelHeight - 6;
                    const arrowHead = `${xSecondary},${y} ${xSecondary - 14},${y - 7} ${xSecondary - 14},${y + 7}`;
                    return (
                      <g key={marker.id} className="multiplier-marker">
                        <line
                          x1={xPrimary}
                          y1={y}
                          x2={xSecondary}
                          y2={y}
                          stroke={themeColors.secondaryAxis}
                          strokeWidth={2}
                        />
                        <polygon
                          points={arrowHead}
                          fill={themeColors.secondaryAxis}
                        />
                        <foreignObject
                          x={labelX}
                          y={labelY}
                          width={totalWidth}
                          height={labelHeight}
                          style={{ overflow: 'visible', pointerEvents: 'auto' }}
                          onClick={toggleLabel}
                          onMouseDown={(e) => e.stopPropagation()}
                          onTouchStart={(e) => e.stopPropagation()}
                        >
                          {isLabelVisible ? (
                            <div
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: `${gap}px`,
                                fontSize: `${fontSize}px`,
                                fontWeight: 600,
                                color: themeColors.secondaryLabel,
                                cursor: 'pointer',
                              }}
                            >
                              <div style={{ width: `${symbolWidth}px`, textAlign: 'right' }}>
                                <MathRenderer expression={multiplierLabelData.latexSymbol} />
                              </div>
                              <div style={{ width: `${valueWidth}px`, textAlign: 'left' }}>
                                <MathRenderer expression={multiplierLabelData.latexValue} />
                              </div>
                            </div>
                          ) : (
                            <div style={{
                              width: '16px',
                              height: '16px',
                              borderRadius: '9999px',
                              border: `1px dashed ${themeColors.secondaryLabel}`,
                              background: 'transparent',
                              margin: '0 auto',
                              cursor: 'pointer',
                            }} />
                          )}
                        </foreignObject>
                      </g>
                    );
                  }
                })}
              </g>
            )}
            
            {/* All ticks - rendered with hierarchical level-based styling */}
            {/* First render non-major ticks (minor/subticks) */}
            {minorVisibleTicks.map((tick, index) => {
              const tickPos = valueToPixel(tick.value);
              const valueKey = tick.value.toString();
              const isOriginTick = Math.abs(tick.value) < 1e-10;
              const isCustom = tick.source === 'custom' || tick.source === 'midpoint';
              const tickLevel = tick.level ?? 3; // Default to level 3 for minor ticks
              const renderLevel =
                subdivisionMode === 'halves-tenths' && tickLevel === 3 ? 1 : tickLevel;
              const style = getTickStyleByLevel(renderLevel, isOriginTick, isCustom, isDark);
              const labelColor = equivalentFractions ? style.color : themeColors.ticks.level0;
              
              // Check label visibility (same logic as major ticks)
              const currentHiddenOverride = getPrimaryHidden(valueKey);
              const baseVisible = currentHiddenOverride !== true;
              const isLabelVisible = mainLabelsHidden ? !baseVisible : baseVisible;
              
              // Calculate tick coordinates based on orientation
              const x1 = isHorizontal ? tickPos : lineX - style.height / 2;
              const y1 = isHorizontal ? lineY - style.height / 2 : tickPos;
              const x2 = isHorizontal ? tickPos : lineX + style.height / 2;
              const y2 = isHorizontal ? lineY + style.height / 2 : tickPos;
              
              return (
                <g key={`tick-${tick.value}-${index}`} className={`tick-level-${tickLevel}`}>
                  <line
                    x1={x1}
                    y1={y1}
                    x2={x2}
                    y2={y2}
                    stroke={style.color}
                    strokeWidth={style.strokeWidth}
                    className="hierarchical-tick"
                  />
                  {/* Show label for minor ticks if showLabel is true AND not hidden - clickable to toggle */}
                  {tick.showLabel && tick.label && isLabelVisible && (
                    <g 
                      className="label-group cursor-pointer"
                      onClick={() => handleLabelClick(tick.value)}
                      onMouseDown={(e) => e.stopPropagation()}
                      style={{ cursor: 'pointer' }}
                    >
                      <rect
                        x={isHorizontal ? tickPos - 20 : (isCustom ? lineX - 65 : lineX - 55)}
                        y={isHorizontal ? lineY + minorTapOffset : tickPos - 10}
                        width="40"
                        height="20"
                        fill="transparent"
                        className="label-click-area"
                      />
                      <TickLabel
                        tick={tick}
                        locale={locale}
                        numberType={
                          equivalentFractions
                            ? (equivalentFractions.labelFormat === 'decimal' ? 'decimal' : 'fraction')
                            : numberLine.numberType
                        }
                        x={isHorizontal ? tickPos : (isCustom ? lineX - 40 : lineX - 15)}
                        y={isHorizontal ? lineY + minorLabelOffset : tickPos}
                        textAnchor={isHorizontal ? "middle" : "end"}
                        dominantBaseline={isHorizontal ? "hanging" : "middle"}
                        className="tick-label minor-label"
                        style={{
                          fontSize: `${14 * textScale}px`,
                          fontWeight: '400',
                          fill: labelColor,
                          userSelect: 'none',
                          pointerEvents: 'none'
                        }}
                      />
                    </g>
                  )}
                  {/* Hidden label placeholder for minor ticks - clickable to restore */}
                  {tick.showLabel && tick.label && !isLabelVisible && (
                    <g
                      className="label-placeholder-group cursor-pointer"
                      onClick={() => handleLabelClick(tick.value)}
                      onMouseDown={(e) => e.stopPropagation()}
                      style={{ cursor: 'pointer' }}
                    >
                      <rect
                        x={isHorizontal ? tickPos - 15 : (isCustom ? lineX - 65 : lineX - 35)}
                        y={isHorizontal ? lineY + minorTapOffset : tickPos - 10}
                        width={isHorizontal ? 30 : (isCustom ? 50 : 30)}
                        height="20"
                        fill="transparent"
                        className="label-click-area"
                      />
                      <circle
                        cx={isHorizontal ? tickPos : (isCustom ? lineX - 40 : lineX - 20)}
                        cy={isHorizontal ? lineY + minorPlaceholderOffset : tickPos}
                        r="6"
                        fill={themeColors.placeholderFill}
                        stroke={themeColors.placeholderStroke}
                        strokeWidth="1"
                        strokeDasharray="2,2"
                        className="label-placeholder minor"
                      />
                    </g>
                  )}
                </g>
              );
            })}
            
            {/* Major ticks - ENHANCED with hierarchical level styling */}
            {majorVisibleTicks.map((tick, index) => {
              const tickPos = valueToPixel(tick.value);
              const valueKey = tick.value.toString();
              const tickLevel = tick.level ?? 0;

              // Base visibility: what this label would do without the global toggle,
              // controlled only by its individual hide/show override.
              const currentHiddenOverride = getPrimaryHidden(valueKey);
              const baseVisible = currentHiddenOverride !== true;

              // Global toggle flips every label from its base state instead of
              // forcing all on/off. This means mixed configurations stay mixed
              // when you use the pan toolbar "Toggle Labels" button.
              const isLabelVisible = mainLabelsHidden ? !baseVisible : baseVisible;
              
              // Determine tick styling based on level and source
              const isOriginTick = Math.abs(tick.value) < 1e-10;
              const isCustom = tick.source === 'custom' || tick.source === 'midpoint';
              const renderLevel =
                subdivisionMode === 'halves-tenths' && tickLevel === 3 ? 1 : tickLevel;
              const style = getTickStyleByLevel(renderLevel, isOriginTick, isCustom, isDark);
              const labelColor = equivalentFractions ? style.color : themeColors.ticks.level0;
              
              // Calculate tick coordinates based on orientation
              const x1 = isHorizontal ? tickPos : lineX - style.height / 2;
              const y1 = isHorizontal ? lineY - style.height / 2 : tickPos;
              const x2 = isHorizontal ? tickPos : lineX + style.height / 2;
              const y2 = isHorizontal ? lineY + style.height / 2 : tickPos;
              
              return (
                <g key={`major-${tick.value}-${index}`} className={`major-tick-group tick-level-${tickLevel}`}>
                  {/* Major tick line with level-based styling */}
                  <line
                    x1={x1}
                    y1={y1}
                    x2={x2}
                    y2={y2}
                    stroke={style.color}
                    strokeWidth={style.strokeWidth}
                    className="major-tick hierarchical-tick"
                  />
                  
                  {/* Clickable label area */}
                  {isLabelVisible && (
                    <rect
                      x={isHorizontal ? tickPos - 20 : (isCustom ? lineX - 80 : lineX - 60)}
                      y={isHorizontal ? lineY + majorTapOffset : tickPos - 15}
                      width={isHorizontal ? 40 : (isCustom ? 60 : 50)}
                      height="30"
                      fill="transparent"
                      className="label-click-area cursor-pointer"
                      onClick={() => handleLabelClick(tick.value)}
                      onMouseDown={(e) => e.stopPropagation()}
                      style={{ cursor: 'pointer' }}
                    />
                  )}
                  
                  {/* T-10: Show delete button for custom ticks */}
                  {isCustom && (
                    <circle
                      cx={isHorizontal ? tickPos : lineX - 25}
                      cy={isHorizontal ? lineY - 25 : tickPos}
                      r="8"
                      fill="#7c3aed"
                      stroke="white"
                      strokeWidth="1"
                      className="custom-tick-delete cursor-pointer"
                      onClick={(e) => {
                        e.stopPropagation();
                        setContextMenu({
                          visible: true,
                          position: { x: e.clientX, y: e.clientY },
                          mode: 'delete-tick',
                          tickToDelete: tick
                        });
                      }}
                      style={{ cursor: 'pointer' }}
                    />
                  )}
                  
                  {/* Label with locale support - size based on level */}
                  {isLabelVisible && tick.showLabel && (
                    <g 
                      className="label-group cursor-pointer"
                      onClick={() => handleLabelClick(tick.value)}
                      onMouseDown={(e) => e.stopPropagation()}
                      style={{ cursor: 'pointer' }}
                    >
                      {/* Invisible clickable area behind label */}
                      {/* For custom ticks in vertical mode, position further left to avoid clashing with marker */}
                      <rect
                        x={isHorizontal ? tickPos - 25 : (isCustom ? lineX - 75 : lineX - 55)}
                        y={isHorizontal ? lineY + majorLabelHitOffset : tickPos - 12}
                        width="50"
                        height="25"
                        fill="transparent"
                        className="label-click-area"
                      />
                      <TickLabel
                        tick={tick}
                        locale={locale}
                        numberType={
                          equivalentFractions
                            ? (equivalentFractions.labelFormat === 'decimal' ? 'decimal' : 'fraction')
                            : numberLine.numberType
                        }
                        x={isHorizontal ? tickPos : (isCustom ? lineX - 50 : lineX - 15)}
                        y={isHorizontal ? lineY + majorLabelOffset : tickPos}
                        textAnchor={isHorizontal ? "middle" : "end"}
                        dominantBaseline={isHorizontal ? "hanging" : "middle"}
                        className="tick-label"
                        style={{
                          fontSize: `${(tickLevel === 0 ? 18 : tickLevel === 1 ? 16 : 14) * textScale}px`,
                          fontWeight: tickLevel === 0 ? '600' : '500',
                          fill: labelColor,
                          userSelect: 'none',
                          pointerEvents: 'none'
                        }}
                      />
                    </g>
                  )}
                  
                  {/* Hidden label placeholder (still clickable) - only for level 0 ticks */}
                  {!isLabelVisible && tickLevel === 0 && (
                    <>
                      {/* Larger clickable area around the circle */}
                      <rect
                        x={isHorizontal ? tickPos - 20 : (isCustom ? lineX - 80 : lineX - 40)}
                        y={isHorizontal ? lineY + majorLabelHitOffset : tickPos - 10}
                        width={isHorizontal ? 40 : (isCustom ? 60 : 40)}
                        height="20"
                        fill="transparent"
                        className="label-click-area cursor-pointer"
                        onClick={() => handleLabelClick(tick.value)}
                        onMouseDown={(e) => e.stopPropagation()}
                        style={{ cursor: 'pointer' }}
                      />
                      <circle
                        cx={isHorizontal ? tickPos : (isCustom ? lineX - 50 : lineX - 20)}
                        cy={isHorizontal ? lineY + majorPlaceholderOffset : tickPos}
                        r="10"
                        fill={themeColors.placeholderFill}
                        stroke={themeColors.placeholderStroke}
                        strokeWidth="1"
                        strokeDasharray="2,2"
                        className="label-placeholder"
                        style={{ cursor: 'pointer' }}
                        onClick={() => handleLabelClick(tick.value)}
                        onMouseDown={(e) => e.stopPropagation()}
                      />
                    </>
                  )}
                </g>
              );
            })}
            
            {/* Origin marker */}
            {showOrigin && (
              <g className="origin-marker">
                <circle
                  cx={originX}
                  cy={originY}
                  r="4"
                  fill="#DC2626"
                  className="origin-circle"
                />
                {/* Hide origin label for the compact integer helper line to avoid overlap with the 0 tick */}
                {!labelsAboveLine && (
                  <text
                    x={isHorizontal ? originX : originX + 5}
                    y={isHorizontal ? originY - 10 : originY}
                    textAnchor={isHorizontal ? "middle" : "start"}
                    dominantBaseline={isHorizontal ? "auto" : "middle"}
                    className="origin-label"
                    style={{
                      fontSize: `${12 * textScale}px`,
                      fontWeight: 'bold',
                      fill: '#DC2626',
                      userSelect: 'none'
                    }}
                  >
                    O
                  </text>
                )}
              </g>
            )}
            
            {/* T-11: Constants overlay */}
            {renderConstants()}
            
            {/* T-13: Jump indicators - Primary line */}
            <JumpIndicatorsRenderer
              line="primary"
              isHorizontal={isHorizontal}
              linePosition={isHorizontal ? lineY : lineX}
              valueToPixel={valueToPixel}
              isDark={preferences.theme === 'dark'}
              onDragStart={handleJumpDragStart}
              isJumpDragging={(jumpId: string, handleType?: 'start' | 'end' | 'whole') => {
                if (dragStateRef.current.jumpId !== jumpId) return false;
                if (!dragStateRef.current.isDragging) return false;
                if (handleType && dragStateRef.current.handleType !== handleType) return false;
                return true;
              }}
            />
            
            {/* T-13: Jump indicators - Secondary line (if enabled) */}
            {!equivalentFractions && secondaryLine.enabled && (
              <JumpIndicatorsRenderer
                line="secondary"
                isHorizontal={isHorizontal}
                linePosition={isHorizontal ? secondaryLinePosition.y : secondaryLinePosition.x}
                valueToPixel={valueToPixel}
                isDark={preferences.theme === 'dark'}
                onDragStart={handleJumpDragStart}
                isJumpDragging={(jumpId: string, handleType?: 'start' | 'end' | 'whole') => {
                  if (dragStateRef.current.jumpId !== jumpId) return false;
                  if (!dragStateRef.current.isDragging) return false;
                  if (handleType && dragStateRef.current.handleType !== handleType) return false;
                  return true;
                }}
              />
            )}
            
            {/* T-17: Expression objects */}
            {renderExpressionObjects()}
            
            {/* T-16: Measurement Overlay - moved inside SVG */}
            <MeasurementOverlay
              width={width}
              height={height}
              domain={domain}
              isHorizontal={isHorizontal}
              valueToPixel={valueToPixel}
              lineDenominator={equivalentFractions?.denominator}
              labelFormat={equivalentFractions?.labelFormat}
              decimalPlaces={Math.max(0, Math.min(preferences?.constantsPrecision ?? 2, 6))}
              hideLabels={equivalentFractions ? mainLabelsHidden : false}
            />
            

          </svg>
        </div>
        
        {/* T-18: Annotation Canvas Overlay - Now handled by NumberLineCanvas.tsx */}
        
        {/* T-15: Snap Indicator */}
        {currentSnapResult && currentSnapResult.isSnapping && ui.dragSnap.showIndicators && (
          <SnapIndicator
            isVisible={true}
            snapTarget={currentSnapResult.snapTarget!}
            valueToPixel={valueToPixel}
            isHorizontal={isHorizontal}
            linePosition={{ x: lineX, y: lineY }}
          />
        )}
        
        {/* DNL: Add Point Dialog */}
        <AddPointDialog
          isOpen={addPointDialog.isOpen}
          onClose={() => dispatch(closeAddPointDialog())}
          initialLine={addPointDialog.targetLine}
          initialValue={addPointDialog.initialValue}
          isDark={isDark}
        />

        {/* DNL: Edit Point Dialog */}
        <EditPointDialog
          isOpen={contextMenu.mode === 'edit-tick' && contextMenu.tickToEdit !== undefined}
          onClose={() => setContextMenu({
            visible: false,
            position: { x: 0, y: 0 },
            mode: 'add-midpoint',
            intervalValues: undefined,
            tickToDelete: undefined,
            tickToEdit: undefined,
          })}
          tick={contextMenu.tickToEdit || null}
          isDark={isDark}
        />

        {/* Expression Widget - T-17 */}
        <ExpressionWidget
          screenToValue={(screenPos) => {
            // Convert screen coordinates back to number line value
            // This is a simplified conversion - you might need to adjust based on your coordinate system
            const rect = svgRef.current?.getBoundingClientRect();
            if (!rect) return 0;
            
            const relativeX = screenPos.x - rect.left;
            const relativeY = screenPos.y - rect.top;
            
            return isHorizontal 
              ? pxToValueHelper(relativeX)
              : pxToValueHelper(relativeY);
          }}
          domain={domain}
        />
        
      </div>

      {/* T-17: Enhanced Context Menu with expression support */}
      {/* Use Portal to render outside any transformed containers for correct fixed positioning */}
      {contextMenu.visible && createPortal(
        <EnhancedContextMenu
          position={contextMenu.position}
          visible={contextMenu.visible}
          onClose={() => setContextMenu({ 
            visible: false, 
            position: { x: 0, y: 0 },
            mode: 'add-midpoint',
            intervalValues: undefined,
            tickToDelete: undefined
          })}
          onAddMidpoint={
            contextMenu.mode === 'add-midpoint' && contextMenu.intervalValues
              ? () => dispatch(addMidpoint({ 
                  startValue: contextMenu.intervalValues!.start, 
                  endValue: contextMenu.intervalValues!.end,
                  equivalentDenominator: equivalentFractions?.denominator,
                }))
              : undefined
          }
          onAddMultiplier={
            secondaryLine.enabled
              ? (contextMenu.mode === 'add-midpoint' && lastRightClickPosition)
                ? () => addMultiplierMarker(lastRightClickPosition.value)
                : (contextMenu.mode === 'edit-tick' && contextMenu.tickToEdit && contextMenu.isDNLTick)
                ? () => {
                    // Add multiplier marker at the tick's value
                    const tickValue = contextMenu.tickToEdit.value;
                    addMultiplierMarker(tickValue);
                    setContextMenu({
                      visible: false,
                      position: { x: 0, y: 0 },
                      mode: 'add-midpoint',
                      intervalValues: undefined,
                      tickToDelete: undefined,
                      tickToEdit: undefined,
                    });
                  }
                : undefined
              : undefined
          }
          onEditTick={
            contextMenu.tickToEdit && contextMenu.mode === 'edit-tick' && contextMenu.isDNLTick
              ? () => {
                  // Edit dialog is controlled by contextMenu.tickToEdit state
                  // No action needed here, dialog will open automatically
                }
              : undefined
          }
          onDeleteTickOnly={
            contextMenu.tickToDelete && (contextMenu.mode === 'delete-tick' || contextMenu.mode === 'edit-tick') && contextMenu.isDNLTick
              ? () => {
                  const tickId = contextMenu.tickToDelete.id;
                  if (tickId) {
                    // Always dispatch - the reducer handles the case where the tick doesn't exist
                    // This ensures deletion works even if the linked tick was already deleted
                    dispatch(removeDNLCustomTick({ id: tickId, removeLinked: false }));
                  }
                  setContextMenu({
                    visible: false,
                    position: { x: 0, y: 0 },
                    mode: 'add-midpoint',
                    intervalValues: undefined,
                    tickToDelete: undefined,
                    tickToEdit: undefined,
                  });
                }
              : undefined
          }
          onDeleteTickAndLinked={
            contextMenu.tickToDelete && (contextMenu.mode === 'delete-tick' || contextMenu.mode === 'edit-tick') && contextMenu.isDNLTick
              ? () => {
                  const tickId = contextMenu.tickToDelete.id;
                  if (tickId) {
                    // Always dispatch - the reducer handles the case where the tick doesn't exist
                    // This ensures deletion works even if the linked tick was already deleted
                    dispatch(removeDNLCustomTick({ id: tickId, removeLinked: true }));
                  }
                  setContextMenu({
                    visible: false,
                    position: { x: 0, y: 0 },
                    mode: 'add-midpoint',
                    intervalValues: undefined,
                    tickToDelete: undefined,
                    tickToEdit: undefined,
                  });
                }
              : undefined
          }
          onDeleteTick={
            contextMenu.tickToDelete && contextMenu.mode === 'delete-tick' && !contextMenu.isDNLTick
              ? () => {
                  // Delete regular custom tick by value
                  dispatch(
                    removeCustomTickByValue({
                      value: contextMenu.tickToDelete.value,
                      equivalentDenominator: equivalentFractions?.denominator,
                    })
                  );
                  setContextMenu({
                    visible: false,
                    position: { x: 0, y: 0 },
                    mode: 'add-midpoint',
                    intervalValues: undefined,
                    tickToDelete: undefined,
                    tickToEdit: undefined,
                  });
                }
              : undefined
          }
          hasLinkedTick={
            contextMenu.tickToDelete && contextMenu.isDNLTick
              ? (() => {
                  // Look up the tick from current state to get up-to-date linkedTickId
                  const tickId = contextMenu.tickToDelete.id;
                  if (tickId) {
                    const currentTick = dnlTools.sparseMode.customTicks.find(t => t.id === tickId);
                    if (currentTick && currentTick.linkedTickId) {
                      // Also verify the linked tick still exists
                      const linkedTick = dnlTools.sparseMode.customTicks.find(t => t.id === currentTick.linkedTickId);
                      return !!linkedTick;
                    }
                  }
                  return false;
                })()
              : false
          }
          onClearMultipliers={
            multipliers.markers.length > 0 ? clearMultiplierMarkers : undefined
          }
          onAddExpression={
            lastRightClickPosition
              ? () => {
                  dispatch(showExpressionWidget({
                    position: { x: lastRightClickPosition.x, y: lastRightClickPosition.y },
                    mode: 'create',
                    expression: '',
                    equivalentDenominator: equivalentFractions?.denominator ?? null,
                  }));
                }
              : undefined
          }
          onAddCustomPoint={
            lastRightClickPosition && sparseModeEnabled
              ? () => {
                  // Determine which line was clicked (primary or secondary)
                  const rect = svgRef.current?.getBoundingClientRect();
                  if (!rect) return;
                  
                  const relativeX = lastRightClickPosition.x - rect.left;
                  const relativeY = lastRightClickPosition.y - rect.top;
                  
                  const primaryLineDistance = isHorizontal 
                    ? Math.abs(relativeY - lineY)
                    : Math.abs(relativeX - lineX);
                  const secondaryLineDistance = isHorizontal
                    ? Math.abs(relativeY - secondaryLinePosition.y)
                    : Math.abs(relativeX - secondaryLinePosition.x);
                  
                  const clickedLine = secondaryLine.enabled && secondaryLineDistance < primaryLineDistance
                    ? 'secondary'
                    : 'primary';
                  
                  dispatch(openAddPointDialog({
                    targetLine: clickedLine,
                    initialValue: lastRightClickPosition.value,
                  }));
                }
              : undefined
          }
          midpointValue={midpointValue}
          clickValue={lastRightClickPosition?.value}
          multiplierLabel={
            contextMenu.mode === 'edit-tick' && contextMenu.tickToEdit && contextMenu.isDNLTick
              ? multiplierLabelData.text
              : multiplierLabelData.text
          }
        />,
        document.body
      )}

      {/* T-17: Styles for context menu interactions */}
      <style>{`
        .context-menu-item:hover {
          background-color: #f3f4f6;
        }
        
        .expression-indicator {
          transition: opacity 0.2s ease;
        }
        
        .expression-marker {
          transition: filter 0.2s ease;
        }
        
        .expression-widget {
          font-family: system-ui, -apple-system, sans-serif;
        }
      `}</style>

      {/* T-18: Add annotation styles */}
      <style>{annotationStyles}</style>

      {/* Phase 7: Ratio Table Overlay */}
      {secondaryLine.enabled && (
        <RatioTableOverlay
          containerRef={exportRef || containerDivRef}
          isDark={isDark}
        />
      )}

    </>
  );
}